import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaCheck, FaBook, FaBookOpen, FaPlay, FaLightbulb, FaUsers, FaGamepad, FaTimes, FaVolumeUp, FaChevronLeft, FaChevronRight, FaRoad, FaKeyboard, FaInfoCircle } from 'react-icons/fa';
import { } from '../utils/audioUtils';
import { useTheme } from '../contexts/ThemeContext';
import { API_BASE_URL } from '../config/api';
import { getCorrectAudioUrl, hasGeneratedAudio, shouldUseAudioUrlAsIs, generateGrammarAudioUrl } from '../utils/audioUrlMapper';

import { isValidAudioUrl, getAudioUrlDisplayText } from '../utils/lessonDetailAudioHelper';
import FarsiKeyboard from '../components/FarsiKeyboard';

interface RichContentLesson {
  _id?: string;
  id?: string;
  title: string;
  description?: string;
  order: number;
  content: string; // JSON string containing rich content
  goal?: string;
  duration?: string;
  wordBank?: any[];
}

interface PhraseScene {
  persian: string;
  english: string;
  pronunciation?: string;
  audioUrl?: string;
}

interface DialogueScene {
  scene_id: string;
  title: string;
  narration: string;
  dialogue: Array<{
    speaker: string;
    persian: string;
    english: string;
    audioUrl?: string;
  }>;
}

interface RichContent {
  scenes: Array<
    | DialogueScene
    | {
        type: string;
        content: {
          text: string;
          translation?: string;
          transliteration?: string;
        };
        choices?: Array<{
          text: string;
          nextScene: number;
        }>;
        character?: {
          name?: string;
          avatar?: string;
        };
        media?: any;
        audioUrl?: string;
      }
    | PhraseScene
  >;
  activities: Array<{
    type: string;
    prompt?: string;
    question?: string;
    options?: string[];
    answer?: string;
    correctAnswer?: string;
    explanation?: string;
    matchPairs?: Array<{
      left: string;
      right: string;
    }>;
    pairs?: Array<{
      persian: string;
      english: string;
    }>;
    // Legacy format support
    persianPhrases?: string[];
    englishMeanings?: string[];
    correctMatches?: Array<{
      persian: string;
      english: string;
    }>;
    audioUrl?: string;
    _id?: string;
  }>;
  wordBank: Array<{
    persian?: string;
    word?: string;
    pronunciation?: string;
    transliteration?: string;
    english?: string;
    translation?: string;
    audioUrl?: string;
    _id?: string;
  }>;
}

interface Pathway {
  _id: string;
  title: string;
  description: string;
  lessonCount: number;
  lessons: RichContentLesson[];
}

// Function to get emoji for Persian words
const getWordEmoji = (persianWord: string, englishWord: string): string => {
  const word = persianWord || englishWord || '';
  const lowerWord = word.toLowerCase();
  
  // Letter emojis
  if (word === 'الف' || word === 'آ' || lowerWord.includes('alef')) return '🔤';
  if (word === 'ب' || lowerWord.includes('be')) return '🔤';
  if (word === 'پ' || lowerWord.includes('pe')) return '🔤';
  if (word === 'ت' || lowerWord.includes('te')) return '🔤';
  if (word === 'ث' || lowerWord.includes('se')) return '🔤';
  if (word === 'ج' || lowerWord.includes('jim')) return '🔤';
  if (word === 'چ' || lowerWord.includes('che')) return '🔤';
  if (word === 'ح' || lowerWord.includes('he')) return '🔤';
  if (word === 'خ' || lowerWord.includes('khe')) return '🔤';
  if (word === 'د' || lowerWord.includes('dal')) return '🔤';
  if (word === 'ذ' || lowerWord.includes('zal')) return '🔤';
  if (word === 'ر' || lowerWord.includes('re')) return '🔤';
  if (word === 'ز' || lowerWord.includes('ze')) return '🔤';
  if (word === 'ژ' || lowerWord.includes('zhe')) return '🔤';
  if (word === 'س' || lowerWord.includes('sin')) return '🔤';
  if (word === 'ش' || lowerWord.includes('shin')) return '🔤';
  if (word === 'ص' || lowerWord.includes('sad')) return '🔤';
  if (word === 'ض' || lowerWord.includes('zad')) return '🔤';
  if (word === 'ط' || lowerWord.includes('ta')) return '🔤';
  if (word === 'ظ' || lowerWord.includes('za')) return '🔤';
  if (word === 'ع' || lowerWord.includes('ain')) return '🔤';
  if (word === 'غ' || lowerWord.includes('ghain')) return '🔤';
  if (word === 'ف' || lowerWord.includes('fe')) return '🔤';
  if (word === 'ق' || lowerWord.includes('qaf')) return '🔤';
  if (word === 'ک' || lowerWord.includes('kaf')) return '🔤';
  if (word === 'گ' || lowerWord.includes('gaf')) return '🔤';
  if (word === 'ل' || lowerWord.includes('lam')) return '🔤';
  if (word === 'م' || lowerWord.includes('mim')) return '🔤';
  if (word === 'ن' || lowerWord.includes('nun')) return '🔤';
  if (word === 'و' || lowerWord.includes('vav')) return '🔤';
  if (word === 'ه' || lowerWord.includes('he')) return '🔤';
  if (word === 'ی' || lowerWord.includes('ye')) return '🔤';
  
  // Family and people
  if (lowerWord.includes('بابا') || lowerWord.includes('dad') || lowerWord.includes('father')) return '👨';
  if (lowerWord.includes('برادر') || lowerWord.includes('brother')) return '👦';
  if (lowerWord.includes('پدر') || lowerWord.includes('father')) return '👨';
  if (lowerWord.includes('پسر') || lowerWord.includes('son')) return '👦';
  if (lowerWord.includes('تو') || lowerWord.includes('you')) return '👤';
  if (lowerWord.includes('من') || lowerWord.includes('i')) return '👤';
  if (lowerWord.includes('او') || lowerWord.includes('he') || lowerWord.includes('she')) return '👤';
  if (lowerWord.includes('ما') || lowerWord.includes('we')) return '👥';
  if (lowerWord.includes('شما') || lowerWord.includes('you')) return '👥';
  if (lowerWord.includes('آنها') || lowerWord.includes('they')) return '👥';
  
  // Nature and environment
  if (lowerWord.includes('آب') || lowerWord.includes('water')) return '💧';
  if (lowerWord.includes('آسمان') || lowerWord.includes('sky')) return '🌌';
  if (lowerWord.includes('آتش') || lowerWord.includes('fire')) return '🔥';
  if (lowerWord.includes('باغ') || lowerWord.includes('garden')) return '🌳';
  if (lowerWord.includes('جنگل') || lowerWord.includes('forest')) return '🌲';
  if (lowerWord.includes('خورشید') || lowerWord.includes('sun')) return '☀️';
  if (lowerWord.includes('پاییز') || lowerWord.includes('autumn')) return '🍂';
  if (lowerWord.includes('تابستان') || lowerWord.includes('summer')) return '☀️';
  
  // Food and drinks
  if (lowerWord.includes('چای') || lowerWord.includes('tea')) return '🍵';
  if (lowerWord.includes('توت') || lowerWord.includes('mulberry')) return '🍇';
  if (lowerWord.includes('پول') || lowerWord.includes('money')) return '💰';
  
  // Objects and things
  if (lowerWord.includes('بازی') || lowerWord.includes('game')) return '🎮';
  if (lowerWord.includes('بانک') || lowerWord.includes('bank')) return '🏦';
  if (lowerWord.includes('تلفن') || lowerWord.includes('telephone')) return '📞';
  if (lowerWord.includes('چمدان') || lowerWord.includes('suitcase')) return '🧳';
  if (lowerWord.includes('چراغ') || lowerWord.includes('lamp') || lowerWord.includes('light')) return '💡';
  if (lowerWord.includes('جعبه') || lowerWord.includes('box')) return '📦';
  if (lowerWord.includes('جارو') || lowerWord.includes('broom')) return '🧹';
  if (lowerWord.includes('پل') || lowerWord.includes('bridge')) return '🌉';
  
  // Body parts
  if (lowerWord.includes('چشم') || lowerWord.includes('eye')) return '👁️';
  
  // Directions and positions
  if (lowerWord.includes('چپ') || lowerWord.includes('left')) return '⬅️';
  if (lowerWord.includes('بلند') || lowerWord.includes('tall') || lowerWord.includes('loud')) return '📏';
  
  // Time and numbers
  if (lowerWord.includes('بیست') || lowerWord.includes('twenty')) return '2️⃣0️⃣';
  if (lowerWord.includes('پنج') || lowerWord.includes('five')) return '5️⃣';
  if (lowerWord.includes('جمعه') || lowerWord.includes('friday')) return '📅';
  if (lowerWord.includes('دیروز') || lowerWord.includes('yesterday')) return '📅';
  if (lowerWord.includes('فردا') || lowerWord.includes('tomorrow')) return '📅';
  
  // Actions and states
  if (lowerWord.includes('آمد') || lowerWord.includes('came')) return '🚶';
  if (lowerWord.includes('بله') || lowerWord.includes('yes')) return '✅';
  if (lowerWord.includes('اما') || lowerWord.includes('but')) return '🔄';
  if (lowerWord.includes('اگر') || lowerWord.includes('if')) return '❓';
  if (lowerWord.includes('چرا') || lowerWord.includes('why')) return '❓';
  if (lowerWord.includes('چطور') || lowerWord.includes('how')) return '❓';
  if (lowerWord.includes('چند') || lowerWord.includes('how many')) return '❓';
  if (lowerWord.includes('کدام') || lowerWord.includes('which')) return '❓';
  if (lowerWord.includes('کجا') || lowerWord.includes('where')) return '❓';
  if (lowerWord.includes('کی') || lowerWord.includes('when') || lowerWord.includes('who')) return '❓';
  
  // Qualities and descriptions
  if (lowerWord.includes('جوان') || lowerWord.includes('young')) return '👶';
  if (lowerWord.includes('پاک') || lowerWord.includes('clean')) return '✨';
  if (lowerWord.includes('تلخ') || lowerWord.includes('bitter')) return '😖';
  
  // Additional specific mappings for better coverage
  if (lowerWord.includes('ثالث') || lowerWord.includes('third')) return '3️⃣';
  if (lowerWord.includes('ثروت') || lowerWord.includes('wealth')) return '💎';
  if (lowerWord.includes('ثمر') || lowerWord.includes('fruit') || lowerWord.includes('outcome')) return '🍎';
  if (lowerWord.includes('ثقافت') || lowerWord.includes('culture')) return '🎭';
  if (lowerWord.includes('ثابت') || lowerWord.includes('stable') || lowerWord.includes('fixed')) return '📌';
  
  // Default emoji for unknown words
  return '📚';
};

const RichContentLesson: React.FC = () => {
  const { pathwayId: urlPathwayId, lessonId: urlLessonId } = useParams<{ pathwayId: string; lessonId: string }>();
  const navigate = useNavigate();
  const [pathway, setPathway] = useState<Pathway | null>(null);
  const [lesson, setLesson] = useState<RichContentLesson | null>(null);
  const [content, setContent] = useState<RichContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
  const [currentStep, setCurrentStep] = useState<'scenes' | 'wordbank' | 'activities' | 'complete'>('scenes');
  const [completedScenes, setCompletedScenes] = useState<number[]>([]);
  const [activityAnswers, setActivityAnswers] = useState<{ [key: number]: any }>({});
  const [activityResults, setActivityResults] = useState<{ [key: number]: boolean }>({});
  const [showActivityResults, setShowActivityResults] = useState<{ [key: number]: boolean }>({});
  const [matchingPairs, setMatchingPairs] = useState<{ [key: number]: { persian: string; english: string }[] }>({});
  const [firstSelection, setFirstSelection] = useState<{ [key: number]: { type: 'persian' | 'english' | 'left' | 'right'; value: string } | null }>({});
  const [matchedPairs, setMatchedPairs] = useState<{ [key: number]: { persian?: string; english?: string; left?: string; right?: string }[] }>({});
  const [lessonProgress, setLessonProgress] = useState<number>(0);
  const [sectionProgress, setSectionProgress] = useState<{ [key: string]: boolean }>({});
  const [incorrectMatches, setIncorrectMatches] = useState<{ [key: number]: { persian?: string; english?: string; left?: string; right?: string }[] }>({});
  const [showKeyboard, setShowKeyboard] = useState<{ [key: number]: boolean }>({});
  const [isLessonCompleted, setIsLessonCompleted] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(0);
  
  // Memoized calculations for better performance
  const scenes = useMemo(() => content?.scenes || [], [content?.scenes]);
  const activities = useMemo(() => content?.activities || [], [content?.activities]);
  const wordBank = useMemo(() => content?.wordBank || [], [content?.wordBank]);
  
  const currentScene = useMemo(() => {
    return scenes[currentSceneIndex] || null;
  }, [scenes, currentSceneIndex]);
  
  const lessonProgressPercentage = useMemo(() => {
    if (!content) return 0;
    const totalSections = scenes.length + activities.length + 1; // +1 for word bank
    const completedSections = completedScenes.length + Object.keys(activityResults).filter(key => activityResults[key]).length + (sectionProgress['wordbank'] ? 1 : 0);
    return Math.round((completedSections / totalSections) * 100);
  }, [content, scenes.length, activities.length, completedScenes.length, activityResults, sectionProgress]);

  // Memoized step configuration
  const steps = useMemo(() => [
    { key: 'scenes', label: 'Scenes', icon: FaBook, color: 'blue', description: 'Interactive learning scenarios' },
    { key: 'wordbank', label: 'Word Bank', icon: FaLightbulb, color: 'green', description: 'Vocabulary and phrases' },
    { key: 'activities', label: 'Activities', icon: FaGamepad, color: 'purple', description: 'Practice exercises' },
    { key: 'complete', label: 'Complete', icon: FaCheck, color: 'emerald', description: 'Lesson finished' }
  ], []);

  // Keyboard shortcuts handler
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in input fields
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          if (currentStep === 'scenes' && currentSceneIndex > 0) {
            handlePrevScene();
          } else if (currentStep !== 'scenes') {
            handlePreviousStep();
          }
          break;
        case 'ArrowRight':
          event.preventDefault();
          if (currentStep === 'scenes' && currentSceneIndex < (content?.scenes?.length || 0) - 1) {
            handleNextScene();
          } else if (currentStep !== 'complete') {
            handleNextStep();
          }
          break;
        case '1':
          event.preventDefault();
          setCurrentStep('scenes');
          break;
        case '2':
          event.preventDefault();
          if (content?.wordBank && content.wordBank.length > 0) {
            setCurrentStep('wordbank');
          }
          break;
        case '3':
          event.preventDefault();
          if (content?.activities && content.activities.length > 0) {
            setCurrentStep('activities');
          }
          break;
        case 'Escape':
          event.preventDefault();
          setShowKeyboardShortcuts(false);
          break;
        case '?':
          event.preventDefault();
          setShowKeyboardShortcuts(!showKeyboardShortcuts);
          break;
        case 'r':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            handleRefresh();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStep, currentSceneIndex, content]);

  const handleRefresh = () => {
    console.log('🔄 Force refreshing lesson data...');
    setForceRefresh(prev => prev + 1);
  };

  // Keyboard shortcuts modal
  const renderKeyboardShortcuts = () => {
    if (!showKeyboardShortcuts) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="relative bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-8 max-w-2xl w-full border border-white/20 overflow-hidden">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 opacity-50"></div>
          
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full -translate-y-12 translate-x-12 opacity-40"></div>
          <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-pink-100 to-purple-100 rounded-full translate-y-10 -translate-x-10 opacity-40"></div>
          
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-800 flex items-center">
                <FaKeyboard className="mr-3 text-blue-500 text-2xl" />
                Keyboard Shortcuts
              </h3>
              <button
                onClick={() => setShowKeyboardShortcuts(false)}
                className="p-2 hover:bg-gray-200 rounded-full transition-colors duration-200"
              >
                <FaTimes className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="font-bold text-gray-700 text-lg">Navigation</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Previous Scene/Step</span>
                    <kbd className="px-3 py-1 bg-gray-200 rounded-lg font-mono text-sm">←</kbd>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Next Scene/Step</span>
                    <kbd className="px-3 py-1 bg-gray-200 rounded-lg font-mono text-sm">→</kbd>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Go to Scenes</span>
                    <kbd className="px-3 py-1 bg-gray-200 rounded-lg font-mono text-sm">1</kbd>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Go to Word Bank</span>
                    <kbd className="px-3 py-1 bg-gray-200 rounded-lg font-mono text-sm">2</kbd>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <h4 className="font-bold text-gray-700 text-lg">Actions</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Go to Activities</span>
                    <kbd className="px-3 py-1 bg-gray-200 rounded-lg font-mono text-sm">3</kbd>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Refresh Lesson</span>
                    <kbd className="px-3 py-1 bg-gray-200 rounded-lg font-mono text-sm">Ctrl+R</kbd>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Show Shortcuts</span>
                    <kbd className="px-3 py-1 bg-gray-200 rounded-lg font-mono text-sm">?</kbd>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Close Modal</span>
                    <kbd className="px-3 py-1 bg-gray-200 rounded-lg font-mono text-sm">Esc</kbd>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl border border-blue-200">
              <p className="text-sm text-gray-600 text-center">
                💡 <strong>Tip:</strong> Press <kbd className="px-2 py-1 bg-white rounded font-mono text-xs">?</kbd> anytime to see this help
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Calculate lesson progress
  useEffect(() => {
    setLessonProgress(lessonProgressPercentage);
  }, [lessonProgressPercentage]);

  // Mark section as completed
  const markSectionCompleted = (sectionKey: string) => {
    setSectionProgress(prev => ({
      ...prev,
      [sectionKey]: true
    }));
  };

  // Use URL parameters directly
  const pathwayId = urlPathwayId;
  const lessonId = urlLessonId;

  // Debug logging
  console.log('🔍 RichContentLesson debug:', {
    urlPathwayId,
    urlLessonId,
    pathwayId,
    lessonId,
    currentPath: window.location.pathname
  });

  // Debug: Log lesson data when it changes
  useEffect(() => {
    if (lesson) {
      console.log('✅ Lesson state updated:', lesson);
    }
  }, [lesson]);

  // Debug: Log content data when it changes
  useEffect(() => {
    if (content) {
      console.log('✅ Content state updated:', content);
    }
  }, [content]);

  // Debug: Log pathway state when it changes
  useEffect(() => {
    console.log('🔍 Pathway state changed:', pathway);
  }, [pathway]);

  // Debug: Log lesson state when it changes
  useEffect(() => {
    console.log('🔍 Lesson state changed:', lesson);
  }, [lesson]);

  // Load progress from localStorage
  useEffect(() => {
    if (pathwayId && lessonId) {
      const savedProgress = localStorage.getItem(`rich-lesson-${pathwayId}-${lessonId}`);
      if (savedProgress) {
        const progress = JSON.parse(savedProgress);
        setCompletedScenes(progress.completedScenes || []);
        setIsLessonCompleted(progress.isCompleted || false);
        if (progress.isCompleted) {
          setCurrentStep('complete');
        }
      }
    }
  }, [pathwayId, lessonId]);

  // Helper function to detect Persian text
  const isPersianText = (text: string): boolean => {
    const persianRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
    return persianRegex.test(text);
  };

  // Helper function to check if an audio URL is a placeholder file (no longer needed since we have real audio)
  const isPlaceholderAudioFile = (audioUrl: string): boolean => {
    return false; // All audio files are now real
  };

  // Helper function to get text direction
  const getTextDirection = (text: string): 'rtl' | 'ltr' => {
    return isPersianText(text) ? 'rtl' : 'ltr';
  };

  // Helper function to get text alignment
  const getTextAlignment = (text: string): 'right' | 'left' => {
    return isPersianText(text) ? 'right' : 'left';
  };

  // Helper function to ensure English text is always left-to-right
  const getEnglishTextDirection = (): 'ltr' => 'ltr';
  
  // Helper function to ensure English text is always left-aligned
  const getEnglishTextAlignment = (): 'left' => 'left';

  // Save progress to localStorage
  const saveProgress = () => {
    console.log('🔥 saveProgress called!', { pathwayId, lessonId, completedScenes });
    if (!pathwayId || !lessonId) {
      console.log('❌ Missing pathwayId or lessonId:', { pathwayId, lessonId });
      return;
    }
    
    const progress = {
      completedScenes,
      isCompleted: true,
      completedAt: new Date().toISOString()
    };
    const lessonKey = `rich-lesson-${pathwayId}-${lessonId}`;
    console.log('💾 Saving lesson progress:', { lessonKey, progress });
    localStorage.setItem(lessonKey, JSON.stringify(progress));
    
    // Update pathway progress
    const pathwayProgressKey = `pathway-progress-${pathwayId}`;
    const pathwayProgress = localStorage.getItem(pathwayProgressKey);
    const currentProgress = pathwayProgress ? JSON.parse(pathwayProgress) : { completedLessons: [], totalProgress: 0 };
    
    console.log('📊 Current pathway progress:', currentProgress);
    
    if (!currentProgress.completedLessons.includes(lessonId)) {
      currentProgress.completedLessons.push(lessonId);
      // Calculate progress based on actual lesson count from pathway data
      const totalLessons = pathway?.lessonCount || 1;
      currentProgress.totalProgress = Math.min((currentProgress.completedLessons.length / totalLessons) * 100, 100);
      console.log('🎯 Updated pathway progress:', { totalLessons, currentProgress });
      localStorage.setItem(pathwayProgressKey, JSON.stringify(currentProgress));
    }
    
    setIsLessonCompleted(true);
    console.log('✅ Progress saved successfully!');
  };

  const fetchPathwayAndLesson = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      console.log('🔍 Fetching rich lesson:', { pathwayId, lessonId, API_BASE_URL, forceRefresh });
      
      // For rich lessons, use the direct JSON endpoint
      // Remove /api from the path since API_BASE_URL already includes it
      const url = `${API_BASE_URL}/pathways/${pathwayId}/rich-lesson/${lessonId}?t=${Date.now()}&refresh=${forceRefresh}`;
      console.log('🔍 Fetching from URL:', url);
      
      const response = await fetch(url, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      console.log('🔍 Response status:', response.status, response.statusText);
      
      const data = await response.json();
      console.log('🔍 API Response:', data);
      
      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      // Set the lesson data directly
      const foundLesson = data.data;
      if (foundLesson) {
        console.log('✅ Lesson found:', foundLesson.title);
        setLesson(foundLesson);
        
        // Create a minimal pathway object to satisfy the pathway state requirement
        const pathwayInfo = {
          _id: pathwayId || 'unknown',
          title: foundLesson.title || 'Unknown Pathway',
          description: foundLesson.description || '',
          lessonCount: 1,
          lessons: [foundLesson]
        };
        setPathway(pathwayInfo);
        
        // First check if the lesson has content with scenes (prioritize this)
        if (foundLesson.content) {
          try {
            let parsedContent;
            if (typeof foundLesson.content === 'string') {
              console.log('🔍 Parsing JSON content string:', foundLesson.content.substring(0, 200) + '...');
              parsedContent = JSON.parse(foundLesson.content);
            } else {
              console.log('🔍 Using content as object:', foundLesson.content);
              parsedContent = foundLesson.content;
            }
            console.log('✅ Parsed content:', parsedContent);
            console.log('🔍 Parsed content type:', typeof parsedContent);
            console.log('🔍 Parsed content keys:', Object.keys(parsedContent));
            console.log('🔍 Scenes in parsed content:', parsedContent.scenes);
            console.log('🔍 Scenes length:', parsedContent.scenes?.length);
            
            // Check if the parsed content has scenes
            if (parsedContent.scenes && parsedContent.scenes.length > 0) {
              console.log('🎭 Found scenes in content:', parsedContent.scenes.length);
              console.log('🎭 First scene:', parsedContent.scenes[0]);
              console.log('🎭 Scene types:', parsedContent.scenes.map((s: any) => s.type));
              
              // Transform scenes to match the expected format
              const transformedScenes = parsedContent.scenes.map((scene: any, index: number) => {
                console.log(`🎭 Transforming scene ${index}:`, scene);
                
                // Handle scenes with dialogue arrays
                if (scene.dialogue && Array.isArray(scene.dialogue)) {
                  console.log(`🎭 Scene ${index} has dialogue array:`, scene.dialogue.length, 'lines');
                  return {
                    type: 'dialogue',
                    title: scene.title || `Scene ${index + 1}`,
                    narration: scene.narration,
                    dialogue: scene.dialogue.map((line: any) => ({
                      persian: line.persian,
                      english: line.english,
                      speaker: line.speaker,
                      audioUrl: line.audioUrl
                    })),
                    scene_id: scene.scene_id
                  };
                }
                
                // Handle dialogue type scenes with content structure
                if (scene.type === 'dialogue' && scene.content) {
                  console.log(`🎭 Scene ${index} is dialogue type with content:`, scene.content);
                  return scene; // Keep as is, the renderScene function will handle it
                }
                
                // Handle other scene types
                console.log(`🎭 Scene ${index} is other type:`, scene.type);
                return scene;
              });
              
              const enhancedContent = {
                ...parsedContent,
                scenes: transformedScenes
              };
              
              console.log('🎭 Enhanced content with transformed scenes:', enhancedContent);
              console.log('🎭 Final scenes array:', enhancedContent.scenes);
              setContent(enhancedContent);
              return; // Exit early since we found and set content
            } else {
              console.log('⚠️ No scenes found in parsed content, will check for phrases');
              console.log('⚠️ Parsed content structure:', parsedContent);
            }
          } catch (error) {
            console.warn('⚠️ Could not parse content JSON:', error);
            console.error('⚠️ Content parsing error details:', error);
          }
        }
        
        // If the lesson has phrases, use them as fallback when no content scenes are found
        if (foundLesson.phrases && foundLesson.phrases.length > 0) {
          console.log('🎵 Using lesson phrases as fallback content');
          
          // Extract wordBank and activities from content JSON if they exist
          let wordBank = foundLesson.wordBank || [];
          let activities = foundLesson.activities || [];
          
          if (foundLesson.content) {
            try {
              const contentObj = typeof foundLesson.content === 'string' 
                ? JSON.parse(foundLesson.content) 
                : foundLesson.content;
              
              // Extract wordBank if not found directly on lesson
              if (contentObj.wordBank && contentObj.wordBank.length > 0 && !wordBank.length) {
                wordBank = contentObj.wordBank;
                console.log('🔤 Extracted wordBank from content JSON:', wordBank.length, 'words');
              }
              
              // Extract activities if not found directly on lesson
              if (contentObj.activities && contentObj.activities.length > 0 && !activities.length) {
                activities = contentObj.activities;
                console.log('🎯 Extracted activities from content JSON:', activities.length, 'activities');
              }
            } catch (error) {
              console.warn('⚠️ Could not parse content for wordBank/activities:', error);
            }
          }
          
          // Create a simple content structure from phrases
          const phrasesContent = {
            scenes: foundLesson.phrases.map((phrase: any, index: number) => ({
              persian: phrase.persian,
              english: phrase.english,
              pronunciation: phrase.pronunciation,
              audioUrl: phrase.audioUrl
            })),
            activities: activities,
            wordBank: wordBank
          };
          console.log('✅ Created phrases content:', phrasesContent);
          setContent(phrasesContent);
        } else if (!foundLesson.content || (foundLesson.content && typeof foundLesson.content === 'string' && !foundLesson.content.includes('"scenes"'))) {
          console.log('⚠️ No content scenes or phrases found, creating empty content');
          // Create empty content structure
          const emptyContent = {
            scenes: [],
            activities: foundLesson.activities || [],
            wordBank: foundLesson.wordBank || []
          };
          setContent(emptyContent);
        }
      } else {
        console.error(`❌ Lesson not found: lessonId=${lessonId}`);
        throw new Error('Lesson not found');
      }
    } catch (err) {
      console.error('❌ Error fetching lesson:', err);
      if (err instanceof Error && err.name === 'AbortError') {
        // Request was aborted
        console.log('⏱️ Request was aborted due to timeout');
      }
      // Don't navigate away immediately, let the component show the error state
    } finally {
      setLoading(false);
    }
  }, [pathwayId, lessonId, forceRefresh]);

  useEffect(() => {
    if (pathwayId && lessonId) {
      fetchPathwayAndLesson();
    } else {
      console.log('❌ Missing pathwayId or lessonId:', { pathwayId, lessonId });
      setLoading(false);
    }
  }, [pathwayId, lessonId, forceRefresh]);

  // Initialize matching pairs when content loads
  useEffect(() => {
    if (content?.activities) {
      const initialMatchingPairs: { [key: number]: { persian: string; english: string }[] } = {};
      content.activities.forEach((activity: any, index: number) => {
        if (activity.type === 'matching') {
          let activityPairs = [];
          
          // Handle new format: matchPairs or pairs
          if (activity.matchPairs || activity.pairs) {
            activityPairs = activity.matchPairs || activity.pairs;
          } 
          // Handle legacy format: persianPhrases + englishMeanings + correctMatches
          else if (activity.persianPhrases && activity.englishMeanings && activity.correctMatches) {
            activityPairs = activity.correctMatches;
          }
          
          if (activityPairs.length > 0) {
            const convertedPairs = activityPairs.map((pair: any) => ({
              persian: pair.persian || pair.left,
              english: pair.english || pair.right
            }));
            initialMatchingPairs[index] = [...convertedPairs].sort(() => Math.random() - 0.5);
          }
        }
      });
      setMatchingPairs(initialMatchingPairs);
    }
  }, [content]);

  const handleSceneComplete = (sceneIndex: number) => {
    if (!completedScenes.includes(sceneIndex)) {
      setCompletedScenes(prev => [...prev, sceneIndex]);
    }
  };

  const handleMultipleChoiceAnswer = (activityIndex: number, answer: string) => {
    setActivityAnswers(prev => ({
      ...prev,
      [activityIndex]: answer
    }));
  };

  const handleMatchingSelection = (activityIndex: number, type: 'persian' | 'english', value: string) => {
    const currentFirstSelection = firstSelection[activityIndex];
    
    // If no first selection, make this the first selection
    if (!currentFirstSelection) {
      setFirstSelection(prev => ({
        ...prev,
        [activityIndex]: { type, value }
      }));
      return;
    }
    
    // If same type is selected again, change the first selection
    if (currentFirstSelection.type === type) {
      setFirstSelection(prev => ({
        ...prev,
        [activityIndex]: { type, value }
      }));
      return;
    }
    
    // If different type is selected, try to make a match
    const persianValue = type === 'persian' ? value : currentFirstSelection.value;
    const englishValue = type === 'english' ? value : currentFirstSelection.value;
    
    // Check if this is a correct match against the current activity's data
    const currentActivity = content?.activities?.[activityIndex];
    let activityPairs = [];
    if (currentActivity?.matchPairs || currentActivity?.pairs) {
      activityPairs = currentActivity?.matchPairs || currentActivity?.pairs;
    } else if (currentActivity?.correctMatches) {
      activityPairs = currentActivity?.correctMatches;
    }
    
    const isCorrectMatch = activityPairs?.some((pair: any) => {
      const pairPersian = pair.persian || pair.left;
      const pairEnglish = pair.english || pair.right;
      return pairPersian === persianValue && pairEnglish === englishValue;
    });
    
    if (isCorrectMatch) {
      // Add to matched pairs
      setMatchedPairs(prev => ({
        ...prev,
        [activityIndex]: [...(prev[activityIndex] || []), { persian: persianValue, english: englishValue }]
      }));
    } else {
      // Add to incorrect matches temporarily
      setIncorrectMatches(prev => ({
        ...prev,
        [activityIndex]: [...(prev[activityIndex] || []), { persian: persianValue, english: englishValue }]
      }));
      
      // Clear incorrect match after 2 seconds
      setTimeout(() => {
        setIncorrectMatches(prev => ({
          ...prev,
          [activityIndex]: (prev[activityIndex] || []).filter(match => 
            !(match.persian === persianValue && match.english === englishValue)
          )
        }));
      }, 2000);
    }
    
    // Clear first selection
    setFirstSelection(prev => ({
      ...prev,
      [activityIndex]: null
    }));
  };

  const resetMatchingActivity = (activityIndex: number) => {
    setFirstSelection(prev => ({
      ...prev,
      [activityIndex]: null
    }));
    setMatchedPairs(prev => ({
      ...prev,
      [activityIndex]: []
    }));
    setIncorrectMatches(prev => ({
      ...prev,
      [activityIndex]: []
    }));
    setActivityResults(prev => ({
      ...prev,
      [activityIndex]: false
    }));
    setShowActivityResults(prev => ({
      ...prev,
      [activityIndex]: false
    }));
  };

  const toggleKeyboard = (activityIndex: number) => {
    setShowKeyboard(prev => ({
      ...prev,
      [activityIndex]: !prev[activityIndex]
    }));
  };

  const checkActivityResult = (activityIndex: number, activity: any) => {
    if (activity.type === 'multiple-choice' || activity.type === 'video-comprehension' || activity.type === 'cultural-quiz' || 
        activity.type === 'number-recognition' || activity.type === 'cultural-insight' || activity.type === 'cultural-note' ||
        activity.type === 'dialogue-completion') {
      const userAnswer = activityAnswers[activityIndex];
      const correctAnswer = activity.answer || activity.correctAnswer;
      const isCorrect = userAnswer === correctAnswer;
      setActivityResults(prev => ({
        ...prev,
        [activityIndex]: isCorrect
      }));
      setShowActivityResults(prev => ({
        ...prev,
        [activityIndex]: true
      }));
    } else if (activity.type === 'matching') {
      // For matching activities, check if all pairs are matched correctly
      const matchedPairsForActivity = matchedPairs[activityIndex] || [];
      
      // Handle different data formats
      let expectedPairs = [];
      if (activity.matchPairs || activity.pairs) {
        expectedPairs = activity.matchPairs || activity.pairs || [];
      } else if (activity.correctMatches) {
        expectedPairs = activity.correctMatches || [];
      }
      
      const isCorrect = matchedPairsForActivity.length === expectedPairs.length && 
        matchedPairsForActivity.every(match => 
          expectedPairs.some((pair: any) => {
            const pairPersian = pair.persian || pair.left;
            const pairEnglish = pair.english || pair.right;
            return pairPersian === match.persian && pairEnglish === match.english;
          })
        );
      setActivityResults(prev => ({
        ...prev,
        [activityIndex]: isCorrect
      }));
      setShowActivityResults(prev => ({
        ...prev,
        [activityIndex]: true
      }));
    } else if (activity.type === 'fill-in-the-blank') {
      const userAnswer = activityAnswers[activityIndex];
      const correctAnswer = activity.answer || activity.correctAnswer;
      const isCorrect = userAnswer.toLowerCase() === correctAnswer.toLowerCase();
      setActivityResults(prev => ({
        ...prev,
        [activityIndex]: isCorrect
      }));
      setShowActivityResults(prev => ({
        ...prev,
        [activityIndex]: true
      }));
    } else if (activity.type === 'speaking-practice' || activity.type === 'pronunciation-practice' || 
               activity.type === 'real-world-application' || activity.type === 'comprehensive-review' || 
               activity.type === 'scenario-practice') {
      // For practice activities, we'll mark them as completed when they interact with them
      setActivityResults(prev => ({
        ...prev,
        [activityIndex]: true
      }));
      setShowActivityResults(prev => ({
        ...prev,
        [activityIndex]: true
      }));
    }
  };

  const getActivityScore = () => {
    if (!content?.activities) return 0;
    const correctActivities = Object.values(activityResults).filter(result => result).length;
    return Math.round((correctActivities / content.activities.length) * 100);
  };

  const handleNextStep = () => {
    console.log('🚀 handleNextStep called, currentStep:', currentStep);
    if (currentStep === 'scenes') {
      setCurrentStep('wordbank');
    } else if (currentStep === 'wordbank') {
      setCurrentStep('activities');
    } else if (currentStep === 'activities') {
      console.log('🎉 Moving to complete step!');
      setCurrentStep('complete');
      // Save progress when lesson is completed
      saveProgress();
    }
  };

  const handlePreviousStep = () => {
    if (currentStep === 'wordbank') {
      setCurrentStep('scenes');
    } else if (currentStep === 'activities') {
      setCurrentStep('wordbank');
    } else if (currentStep === 'complete') {
      setCurrentStep('activities');
    }
  };

  // Render a single scene
  const renderScene = (scene: any, index: number) => {
    console.log(`🎭 renderScene called for scene ${index}:`, scene);
    console.log(`🎭 Scene type:`, scene?.type);
    console.log(`🎭 Scene content:`, scene?.content);
    console.log(`🎭 Scene dialogue:`, scene?.dialogue);
    console.log(`🎭 Scene scene_id/sceneId:`, scene?.scene_id || scene?.sceneId);
    console.log(`🎭 Scene title:`, scene?.title);
    console.log(`🎭 Scene narration:`, scene?.narration);
    
    const isActive = currentSceneIndex === index;
    const isCompleted = completedScenes.includes(index);

    // Handle scenes with dialogue array (new pathway structure)
    if (scene && typeof scene === 'object' && Array.isArray(scene.dialogue) && scene.dialogue.length > 0) {
      console.log(`🎭 Rendering dialogue scene ${index} with dialogue array`);
      return (
        <div
          key={index}
          className={`bg-white rounded-2xl shadow-lg overflow-hidden border-2 transition-all duration-300 ${
            isActive ? 'border-orange-200' : 'border-gray-200'
          } ${isCompleted ? 'ring-2 ring-green-200' : ''}`}
        >
          {/* Scene Header */}
          <div className="p-6 bg-gradient-to-r from-orange-50 to-yellow-50 border-b border-orange-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-black">
                {scene.title || `Scene ${index + 1}`}
              </h3>
              {isCompleted && (
                <div className="w-8 h-8 bg-green-200 rounded-full flex items-center justify-center">
                  <FaCheck className="text-green-700 text-sm" />
                </div>
              )}
            </div>
          </div>

          {/* Scene Content */}
          <div className="p-6">
            {/* Narration */}
            {scene.narration && (
              <div className="bg-blue-50 rounded-xl p-4 mb-4 text-lg leading-relaxed text-gray-800 shadow-md">
                <div className="flex items-start">
                  <FaBookOpen className="text-blue-600 mr-3 mt-1 flex-shrink-0" />
                  <div>{scene.narration}</div>
                </div>
              </div>
            )}

            {/* Dialogue */}
            {scene.dialogue && scene.dialogue.length > 0 && (
              <div className="space-y-4">
                {scene.dialogue.map((dialogueItem: any, dialogueIndex: number) => (
                  <div key={dialogueIndex} className="bg-orange-50 rounded-xl p-6 shadow-md">
                    <div className="flex items-start space-x-4">
                      {/* Speaker Avatar */}
                      <div className="w-12 h-12 bg-orange-200 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-orange-800 font-bold text-lg">
                          {dialogueItem.speaker?.charAt(0) || '?'}
                        </span>
                      </div>
                      
                      {/* Dialogue Content */}
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h4 className="font-bold text-orange-800 text-lg">
                            {dialogueItem.speaker || 'Speaker'}
                          </h4>
                        </div>
                        
                        {/* Persian Text */}
                        {dialogueItem.persian && (
                          <div 
                            className="text-xl leading-relaxed text-gray-900 mb-2"
                            dir={getTextDirection(dialogueItem.persian)}
                            style={{ textAlign: getTextDirection(dialogueItem.persian) === 'rtl' ? 'right' : 'left' }}
                          >
                            {dialogueItem.persian}
                          </div>
                        )}
                        
                        {/* English Translation */}
                        {dialogueItem.english && (
                          <div className="text-lg text-gray-600 italic mb-3">
                            {dialogueItem.english}
                          </div>
                        )}
                        
                        {/* Audio Button */}
                        {dialogueItem.audioUrl && (
                          <button
                            onClick={() => playAudio(dialogueItem.audioUrl, dialogueItem.persian, 'dialogue', index, dialogueItem.english)}
                            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200"
                          >
                            <FaPlay className="text-sm" />
                            <span>Listen</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Video */}
            {scene.videoUrl && (
              <div className="mt-6">
                <video 
                  controls 
                  className="w-full rounded-xl shadow-lg"
                  src={scene.videoUrl}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            )}

            {/* Scene Actions */}
            <div className="mt-6 flex justify-between items-center">
              <div className="flex space-x-2">
                {scene.dialogue && scene.dialogue.some((d: any) => d.audioUrl) && (
                  <button
                    onClick={() => {
                      const audioUrls = scene.dialogue
                        .filter((d: any) => d.audioUrl)
                        .map((d: any) => d.audioUrl);
                      if (audioUrls.length > 0) {
                        playAudio(audioUrls[0], scene.dialogue[0].persian, 'scene', index);
                      }
                    }}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200"
                  >
                    <FaPlay className="text-sm" />
                    <span>Play All</span>
                  </button>
                )}
              </div>
              
              <button
                onClick={() => {
                  if (!isCompleted) {
                    setCompletedScenes(prev => [...prev, index]);
                  } else {
                    setCompletedScenes(prev => prev.filter(i => i !== index));
                  }
                }}
                className={`px-6 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center space-x-2 ${
                  isCompleted 
                    ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                    : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                }`}
              >
                {isCompleted ? (
                  <span className="flex items-center">
                    <FaCheck className="mr-2" />
                    Completed
                  </span>
                ) : (
                  <span className="flex items-center">
                    <FaCheck className="mr-2" />
                    Mark Complete
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Handle dialogue scenes with the structure from the pathway data (legacy)
    if (scene && typeof scene === 'object' && scene.type === 'dialogue' && scene.content) {
      console.log(`🎭 Rendering dialogue scene ${index} with content structure`);
      return (
        <div
          key={index}
          className={`bg-white rounded-2xl shadow-lg overflow-hidden border-2 transition-all duration-300 ${
            isActive ? 'border-orange-200' : 'border-gray-200'
          } ${isCompleted ? 'ring-2 ring-green-200' : ''}`}
        >
          {/* Scene Header */}
          <div className="p-6 bg-gradient-to-r from-orange-50 to-yellow-50 border-b border-orange-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-black">
                {scene.character?.name || `Scene ${index + 1}`}
              </h3>
              {isCompleted && (
                <div className="w-8 h-8 bg-green-200 rounded-full flex items-center justify-center">
                  <FaCheck className="text-green-700 text-sm" />
                </div>
              )}
            </div>
          </div>

          {/* Scene Content */}
          <div className="p-6">
            {/* Main Content Text */}
            {scene.content && scene.content.text && (
              <div className="bg-orange-50 rounded-xl p-6 mb-4 text-xl leading-relaxed text-gray-900 shadow-md">
                <div 
                  dir={getTextDirection(scene.content.text)}
                  style={{ textAlign: getTextDirection(scene.content.text) === 'rtl' ? 'right' : 'left' }}
                >
                  {scene.content.text}
                </div>
              </div>
            )}
            
            {/* Translation */}
            {scene.content && scene.content.translation && (
              <div className="bg-blue-50 rounded-lg p-4 mb-4 text-lg text-blue-700 shadow-sm">
                <strong>Translation:</strong> {scene.content.translation}
              </div>
            )}
            
            {/* Character Display */}
            {scene.character && (
              <div className="bg-purple-50 rounded-lg p-4 mb-4 flex items-center shadow-sm">
                <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center mr-3">
                  <span className="text-xl text-purple-700 font-bold">
                    {scene.character.name?.charAt(0) || '?'}
                  </span>
                </div>
                <div>
                  <div className="font-semibold text-purple-700">{scene.character.name}</div>
                  <div className="text-sm text-purple-600">Character</div>
                </div>
              </div>
            )}

            {/* Complete Scene Button */}
            <div className="mt-6 text-center">
              <button
                onClick={() => handleSceneComplete(index)}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                  isCompleted
                    ? 'bg-green-200 text-green-900 cursor-default'
                    : 'bg-orange-200 text-orange-900 hover:bg-orange-300 hover:shadow-lg transform hover:scale-105'
                }`}
              >
                {isCompleted ? (
                  <span className="flex items-center justify-center">
                    <FaCheck className="mr-2" />
                    Completed
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <FaCheck className="mr-2" />
                    Mark Complete
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Handle dialogue scenes with dialogue object containing speaker arrays (grammar lesson format)
    if (scene && typeof scene === 'object' && scene.dialogue && typeof scene.dialogue === 'object' && !Array.isArray(scene.dialogue)) {
      console.log(`🎭 Rendering grammar lesson scene ${index} with dialogue object`);
      return (
        <div
          key={index}
          className={`bg-white rounded-2xl shadow-lg overflow-hidden border-2 transition-all duration-300 ${
            isActive ? 'border-orange-200' : 'border-gray-200'
          } ${isCompleted ? 'ring-2 ring-green-200' : ''}`}
        >
          {/* Scene Header */}
          <div className="p-6 bg-gradient-to-r from-orange-50 to-yellow-50 border-b border-orange-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-black">
                {scene.title || `Scene ${index + 1}`}
              </h3>
              {isCompleted && (
                <div className="w-8 h-8 bg-green-200 rounded-full flex items-center justify-center">
                  <FaCheck className="text-green-700 text-sm" />
                </div>
              )}
            </div>
          </div>

          {/* Scene Content */}
          <div className="p-6">
            {/* Vocabulary Section */}
            {scene.vocab && scene.vocab.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <FaBook className="mr-2 text-blue-600" />
                  Key Vocabulary
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {scene.vocab.map((vocabItem: any, vocabIndex: number) => (
                    <div key={vocabIndex} className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div 
                            className="text-lg font-semibold text-gray-900 mb-1"
                            dir={getTextDirection(vocabItem.persian)}
                            style={{ textAlign: getTextDirection(vocabItem.persian) === 'rtl' ? 'right' : 'left' }}
                          >
                            {vocabItem.persian}
                          </div>
                          <div className="text-sm text-gray-600">{vocabItem.english}</div>
                          {vocabItem.transliteration && (
                            <div className="text-xs text-gray-500 italic">{vocabItem.transliteration}</div>
                          )}
                        </div>
                        {vocabItem.audioUrl && (
                          <button
                            onClick={() => playAudio(vocabItem.audioUrl, vocabItem.persian, 'vocab', index, vocabItem.english)}
                            className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg transition-colors duration-200"
                          >
                            <FaPlay className="text-xs" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Dialogue Content */}
            {scene.dialogue && (
              <div className="space-y-4">
                {Object.entries(scene.dialogue).map(([speaker, messages]: [string, any]) => (
                  <div key={speaker} className="bg-orange-50 rounded-xl p-6 shadow-md">
                    <div className="flex items-start space-x-4">
                      {/* Speaker Avatar */}
                      <div className="w-12 h-12 bg-orange-200 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-orange-800 font-bold text-lg">
                          {speaker.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      
                      {/* Dialogue Content */}
                      <div className="flex-1">
                        <div className="flex items-center mb-3">
                          <h4 className="font-bold text-orange-800 text-lg capitalize">
                            {speaker === 'teacher' ? '👨‍🏫 Teacher' : 
                             speaker === 'examples' ? '📝 Examples' :
                             speaker === 'practice' ? '🎯 Practice' :
                             speaker === 'student' ? '👨‍🎓 Student' :
                             speaker}
                          </h4>
                        </div>
                        
                        {/* Messages */}
                        {Array.isArray(messages) && messages.map((message: string, messageIndex: number) => (
                          <div key={messageIndex} className="mb-3 last:mb-0">
                            <div 
                              className="text-lg leading-relaxed text-gray-900"
                              dir={getTextDirection(message)}
                              style={{ textAlign: getTextDirection(message) === 'rtl' ? 'right' : 'left' }}
                            >
                              {message}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Scene Actions */}
            <div className="mt-6 flex justify-between items-center">
              <div className="flex space-x-2">
                {scene.vocab && scene.vocab.some((v: any) => v.audioUrl) && (
                  <button
                    onClick={() => {
                      const audioUrls = scene.vocab
                        .filter((v: any) => v.audioUrl)
                        .map((v: any) => v.audioUrl);
                      if (audioUrls.length > 0) {
                        playAudio(audioUrls[0], scene.vocab[0].persian, 'vocab', index);
                      }
                    }}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200"
                  >
                    <FaPlay className="text-sm" />
                    <span>Play Vocabulary</span>
                  </button>
                )}
              </div>
              
              <button
                onClick={() => {
                  if (!isCompleted) {
                    setCompletedScenes(prev => [...prev, index]);
                  } else {
                    setCompletedScenes(prev => prev.filter(i => i !== index));
                  }
                }}
                className={`px-6 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center space-x-2 ${
                  isCompleted 
                    ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                    : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                }`}
              >
                {isCompleted ? (
                  <span className="flex items-center">
                    <FaCheck className="mr-2" />
                    Completed
                  </span>
                ) : (
                  <span className="flex items-center">
                    <FaCheck className="mr-2" />
                    Mark Complete
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Handle dialogue scenes with dialogue arrays (legacy format)
    if (scene && typeof scene === 'object' && scene.dialogue && Array.isArray(scene.dialogue)) {
      return (
        <div
          key={index}
          className={`bg-white rounded-2xl shadow-lg overflow-hidden border-2 transition-all duration-300 ${
            isActive ? 'border-orange-200' : 'border-gray-200'
          } ${isCompleted ? 'ring-2 ring-green-200' : ''}`}
        >
          {/* Scene Header */}
          <div className="p-6 bg-gradient-to-r from-orange-50 to-yellow-50 border-b border-orange-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-black">
                {scene.title || `Scene ${index + 1}`}
              </h3>
              {isCompleted && (
                <div className="w-8 h-8 bg-green-200 rounded-full flex items-center justify-center">
                  <FaCheck className="text-green-700 text-sm" />
                </div>
              )}
            </div>
          </div>

          {/* Scene Content */}
          <div className="p-6">
            {/* Narration */}
            {scene.narration && (
              <div className="bg-orange-50 rounded-xl p-6 mb-6 text-xl leading-relaxed text-gray-900 shadow-md">
                <div 
                  dir={getTextDirection(scene.narration)}
                  style={{ textAlign: getTextDirection(scene.narration) === 'rtl' ? 'right' : 'left' }}
                >
                  {scene.narration}
                </div>
              </div>
            )}

            {/* Video if present */}
            {scene.videoUrl && (
              <div className="mb-6">
                <div className="bg-gray-100 rounded-lg p-4 mb-4">
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">Video Lesson</h4>
                  <video 
                    controls 
                    className="w-full max-w-full rounded-lg shadow-lg"
                    style={{ maxHeight: '400px' }}
                  >
                    <source src={scene.videoUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            )}
            
            <div className="space-y-4">
              {scene.dialogue.map((line: any, idx: number) => (
                <div key={idx} className="flex flex-col md:flex-row md:items-center md:space-x-4 bg-orange-50 rounded-lg p-4 border border-orange-100">
                  {line.speaker && (
                    <div className="mb-2 md:mb-0 md:w-24">
                      <span className="inline-block bg-orange-200 text-orange-800 text-sm font-semibold px-3 py-1 rounded-full">
                        {line.speaker}
                      </span>
                    </div>
                  )}
                  <div 
                    className="text-2xl font-bold text-gray-900 flex-1"
                    dir="rtl"
                    style={{ textAlign: 'right' }}
                  >
                    {line.persian}
                  </div>
                  <div 
                    dir={getEnglishTextDirection()}
                    className="text-lg text-gray-700 md:ml-4 flex-1"
                    style={{ textAlign: getEnglishTextAlignment() }}
                  >
                    {line.english}
                  </div>
                  {line.audioUrl && (
                    <button
                      onClick={() => playAudio(line.audioUrl, line.persian, 'dialogue', idx, line.english)}
                      className="ml-2 p-2 bg-orange-100 text-orange-900 rounded-full hover:bg-orange-200 transition-colors"
                      title={`Play audio: ${line.audioUrl}`}
                    >
                      <FaVolumeUp />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-6 text-center">
              <button
                onClick={() => handleSceneComplete(index)}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                  isCompleted
                    ? 'bg-green-200 text-green-900 cursor-default'
                    : 'bg-orange-200 text-orange-900 hover:bg-orange-300 hover:shadow-lg transform hover:scale-105'
                }`}
              >
                {isCompleted ? (
                  <span className="flex items-center justify-center">
                    <FaCheck className="mr-2" />
                    Completed
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <FaCheck className="mr-2" />
                    Mark Complete
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Support phrase object scenes
    const isPhraseScene = scene && typeof scene === 'object' && 'persian' in scene && 'english' in scene;
    if (isPhraseScene) {
      return (
        <div
          key={index}
          className={`bg-white rounded-2xl shadow-lg overflow-hidden border-2 transition-all duration-300 ${
            isActive ? 'border-orange-200' : 'border-gray-200'
          } ${isCompleted ? 'ring-2 ring-green-200' : ''}`}
        >
          <div className="p-6 bg-gradient-to-r from-orange-50 to-yellow-50 border-b border-orange-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-black">
                {`Phrase ${index + 1}`}
              </h3>
              {isCompleted && (
                <div className="w-8 h-8 bg-green-200 rounded-full flex items-center justify-center">
                  <FaCheck className="text-green-700 text-sm" />
                </div>
              )}
            </div>
          </div>
          <div className="p-6">
            <div className="mb-6 flex items-center">
              <span 
                className="text-2xl font-bold text-gray-900 mr-4"
                dir="rtl"
                style={{ textAlign: 'right' }}
              >
                {scene.persian}
              </span>
                                  {scene.audioUrl && (
                <button
                  onClick={() => playAudio(scene.audioUrl, scene.persian, 'phrase', index)}
                  className="ml-2 p-2 bg-orange-100 text-orange-900 rounded-full hover:bg-orange-200 transition-colors"
                  title="Play audio"
                >
                  <FaVolumeUp />
                </button>
              )}
            </div>
            {scene.pronunciation && (
              <div 
                className="text-md text-gray-500 italic mb-2"
                dir={getEnglishTextDirection()}
                style={{ textAlign: getEnglishTextAlignment() }}
              >
                {scene.pronunciation}
              </div>
            )}
            <div 
              dir={getEnglishTextDirection()}
              className="text-lg text-gray-700 mb-2"
              style={{ textAlign: getEnglishTextAlignment() }}
            >
              {scene.english}
            </div>
            <div className="mt-6 text-center">
              <button
                onClick={() => handleSceneComplete(index)}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                  isCompleted
                    ? 'bg-green-200 text-green-900 cursor-default'
                    : 'bg-orange-200 text-orange-900 hover:bg-orange-300 hover:shadow-lg transform hover:scale-105'
                }`}
              >
                {isCompleted ? (
                  <span className="flex items-center justify-center">
                    <FaCheck className="mr-2" />
                    Completed
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <FaCheck className="mr-2" />
                    Mark Complete
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (scene && typeof scene === 'object' && 'type' in scene && 'content' in scene) {
      const legacyScene = scene as {
        type: string;
        content: {
          text: string;
          translation?: string;
          transliteration?: string;
        };
        choices?: Array<{ text: string; nextScene: number }>;
        character?: { name?: string; avatar?: string };
        media?: any;
        audioUrl?: string;
        narration?: string;
        teacherTip?: string;
        teacherNote?: string;
        task?: string;
        vocab?: any[];
        dialogue?: any;
        title?: string;
      };
      return (
        <div
          key={index}
          className={`bg-white rounded-2xl shadow-lg overflow-hidden border-2 transition-all duration-300 ${
            isActive ? 'border-orange-200' : 'border-gray-200'
          } ${isCompleted ? 'ring-2 ring-green-200' : ''}`}
        >
          {/* Scene Header */}
          <div className="p-6 bg-gradient-to-r from-orange-50 to-yellow-50 border-b border-orange-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-black">
                {legacyScene.title || `Scene ${index + 1}`}
              </h3>
              {isCompleted && (
                <div className="w-8 h-8 bg-green-200 rounded-full flex items-center justify-center">
                  <FaCheck className="text-green-700 text-sm" />
                </div>
              )}
            </div>
          </div>

          {/* Scene Content */}
          <div className="p-6">
            {/* Scene Type and Content */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FaBook className="mr-2 text-orange-400" />
                {legacyScene.type === 'narration' ? 'Narration' : 
                 legacyScene.type === 'dialogue' ? 'Dialogue' : 
                 legacyScene.type === 'choice' ? 'Interactive Choice' : 
                 legacyScene.type === 'media' ? 'Media' : 
                 legacyScene.type === 'grammar-focus' ? 'Grammar Focus' : 'Content'}
              </h4>
              
              {/* Main Content Text */}
              {legacyScene.content && legacyScene.content.text && (
                <div className="bg-orange-50 rounded-xl p-6 mb-4 text-xl leading-relaxed text-gray-900 shadow-md">
                  {legacyScene.content.text}
                </div>
              )}
              
              {/* Translation */}
              {legacyScene.content && legacyScene.content.translation && (
                <div className="bg-blue-50 rounded-lg p-4 mb-4 text-lg text-white shadow-sm">
                  <strong>Translation:</strong> {legacyScene.content.translation}
                </div>
              )}
              
              {/* Transliteration */}
              {legacyScene.content && legacyScene.content.transliteration && (
                <div className="bg-green-50 rounded-lg p-4 mb-4 text-lg text-green-700 shadow-sm">
                  <strong>Transliteration:</strong> {legacyScene.content.transliteration}
                </div>
              )}
              
              {/* Character Display */}
              {legacyScene.character && (
                <div className="bg-purple-50 rounded-lg p-4 mb-4 flex items-center shadow-sm">
                  <span className="text-2xl mr-3">{legacyScene.character.avatar}</span>
                  <div>
                    <div className="font-semibold text-purple-700">{legacyScene.character.name}</div>
                    <div className="text-sm text-purple-600">Grammar Instructor</div>
                  </div>
                </div>
              )}
              
              {/* Main Narration/Text */}
              {legacyScene.narration && (
                <div className="bg-orange-50 rounded-xl p-6 mb-4 text-2xl leading-relaxed text-gray-900 shadow-md">
                  {legacyScene.narration}
                </div>
              )}
              {/* Teacher Tip/Note */}
              {legacyScene.teacherTip && (
                <div className="bg-orange-50 rounded-lg p-4 mb-4 text-lg text-orange-700 font-semibold shadow-sm">
                  {legacyScene.teacherTip}
                </div>
              )}
              {legacyScene.teacherNote && (
                <div className="bg-green-50 rounded-lg p-4 mb-4 text-lg text-green-700 font-semibold shadow-sm">
                  {legacyScene.teacherNote}
                </div>
              )}
              {/* Choices for Interactive Scenes */}
              {legacyScene.choices && (
                <div className="space-y-4 mt-6">
                  {legacyScene.choices.map((choice: any, idx: number) => (
                    <div key={idx} className="bg-white rounded-lg p-4 border-2 border-orange-100 text-xl text-gray-900 cursor-pointer hover:bg-orange-50 transition-all">
                      {choice.text}
                    </div>
                  ))}
                </div>
              )}
              {/* Matching Task */}
              {legacyScene.task && (
                <div className="bg-yellow-50 rounded-lg p-4 mb-4 text-lg text-yellow-700 font-semibold shadow-sm">
                  {legacyScene.task}
                </div>
              )}
            </div>

            {/* Legacy vocabulary support (if present) */}
            {legacyScene.vocab && Array.isArray(legacyScene.vocab) && legacyScene.vocab.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FaBook className="mr-2 text-green-400" />
                  Vocabulary
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {legacyScene.vocab.map((word: any, wordIndex: number) => (
                    <div key={wordIndex} className="bg-gray-50 rounded-lg p-4">
                      <div className="text-lg font-bold text-gray-900 mb-1">
                        {word.persian}
                      </div>
                      <div className="text-sm text-gray-600 mb-1">
                        {word.transliteration}
                      </div>
                      <div className="text-sm text-gray-500">
                        {word.english}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Legacy dialogue support (if present) */}
            {legacyScene.dialogue && typeof legacyScene.dialogue === 'object' && Object.keys(legacyScene.dialogue).length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FaUsers className="mr-2 text-blue-400" />
                  Dialogue
                </h4>
                <div className="space-y-4">
                  {Object.entries(legacyScene.dialogue).map(([speaker, lines]: [string, any]) => (
                    <div key={speaker} className="bg-gray-50 rounded-lg p-4">
                      <div className="font-semibold text-blue-700 mb-2">
                        {speaker}:
                      </div>
                      {Array.isArray(lines) && lines.map((line: string, lineIndex: number) => (
                        <div key={lineIndex} className="text-gray-700 mb-2">
                          {line}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Complete Scene Button */}
            <div className="mt-6 text-center">
              <button
                onClick={() => handleSceneComplete(index)}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                  isCompleted
                    ? 'bg-green-200 text-green-900 cursor-default'
                    : 'bg-orange-200 text-orange-900 hover:bg-orange-300 hover:shadow-lg transform hover:scale-105'
                }`}
              >
                {isCompleted ? (
                  <span className="flex items-center justify-center">
                    <FaCheck className="mr-2" />
                    Completed
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <FaCheck className="mr-2" />
                    Mark Complete
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Enhanced syllable scene types
    if (scene && typeof scene === 'object' && scene.type === 'syllable-practice' && scene.exercises) {
      return (
        <div
          key={index}
          className={`bg-white rounded-2xl shadow-lg overflow-hidden border-2 transition-all duration-300 ${
            isActive ? 'border-orange-200' : 'border-gray-200'
          } ${isCompleted ? 'ring-2 ring-green-200' : ''}`}
        >
          <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-black flex items-center">
                🎧 {scene.title || 'Interactive Syllable Practice'}
              </h3>
              {isCompleted && (
                <div className="w-8 h-8 bg-green-200 rounded-full flex items-center justify-center">
                  <FaCheck className="text-green-700 text-sm" />
                </div>
              )}
            </div>
          </div>
          <div className="p-6">
            <p className="text-blue-700 mb-6">{scene.instructions}</p>
            
            <div className="grid gap-6">
              {scene.exercises.map((exercise: any, exIndex: number) => (
                <div key={exIndex} className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <span 
                        className="text-3xl font-bold text-gray-900"
                        dir="rtl"
                        style={{ fontFamily: 'Vazirmatn, serif' }}
                      >
                        {exercise.word}
                      </span>
                      {exercise.audioUrl && (
                        <button
                          onClick={() => playAudio(exercise.audioUrl, exercise.word, 'syllable-practice', exIndex)}
                          className="p-3 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
                          title="Play pronunciation"
                        >
                          <FaVolumeUp className="text-lg" />
                        </button>
                      )}
                      {exercise.slowAudioUrl && (
                        <button
                          onClick={() => playAudio(exercise.slowAudioUrl, exercise.word, 'syllable-slow', exIndex)}
                          className="p-3 bg-purple-100 text-purple-700 rounded-full hover:bg-purple-200 transition-colors"
                          title="Play slow pronunciation"
                        >
                          🐌
                        </button>
                      )}
                    </div>
                    <span className="text-gray-600 italic">({exercise.translation})</span>
                  </div>
                  
                  {/* Visual syllable breakdown */}
                  <div className="bg-white rounded-lg p-4 mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-3">Syllable breakdown:</p>
                    <div className="flex items-center justify-center space-x-2">
                      {exercise.syllables.map((syllable: string, sylIndex: number) => (
                        <div key={sylIndex} className="flex items-center">
                          <div className="bg-gradient-to-br from-orange-100 to-yellow-100 border-2 border-orange-200 rounded-lg px-4 py-2">
                            <span 
                              className="text-xl font-bold text-gray-800"
                              style={{ fontFamily: 'Vazirmatn, serif' }}
                            >
                              {syllable}
                            </span>
                            <div className="text-xs text-gray-600 text-center mt-1">
                              {exercise.patterns[sylIndex]}
                            </div>
                          </div>
                          {sylIndex < exercise.syllables.length - 1 && (
                            <span className="text-orange-400 text-2xl mx-2">-</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="text-center text-gray-600">
                    <span className="italic">{exercise.pronunciation}</span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-6 text-center">
              <button
                onClick={() => handleSceneComplete(index)}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                  isCompleted
                    ? 'bg-green-200 text-green-900 cursor-default'
                    : 'bg-blue-200 text-blue-900 hover:bg-blue-300 hover:shadow-lg transform hover:scale-105'
                }`}
              >
                {isCompleted ? (
                  <span className="flex items-center justify-center">
                    <FaCheck className="mr-2" />
                    Completed
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <FaCheck className="mr-2" />
                    Mark Complete
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Interactive explanation scene
    if (scene && typeof scene === 'object' && scene.type === 'interactive-explanation' && scene.patterns) {
      return (
        <div
          key={index}
          className={`bg-white rounded-2xl shadow-lg overflow-hidden border-2 transition-all duration-300 ${
            isActive ? 'border-orange-200' : 'border-gray-200'
          } ${isCompleted ? 'ring-2 ring-green-200' : ''}`}
        >
          <div className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 border-b border-purple-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-black flex items-center">
                🏗️ {scene.title || 'Syllable Patterns'}
              </h3>
              {isCompleted && (
                <div className="w-8 h-8 bg-green-200 rounded-full flex items-center justify-center">
                  <FaCheck className="text-green-700 text-sm" />
                </div>
              )}
            </div>
          </div>
          <div className="p-6">
            {scene.dialogue && scene.dialogue.map((line: any, dialogueIndex: number) => (
              <div key={dialogueIndex} className="mb-4 bg-purple-50 rounded-lg p-4 border border-purple-100">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <span 
                      className="text-xl font-bold text-gray-900 mr-4"
                      dir="rtl"
                      style={{ fontFamily: 'Vazirmatn, serif' }}
                    >
                      {line.persian}
                    </span>
                    <span className="text-gray-700">{line.english}</span>
                  </div>
                  {line.audioUrl && (
                    <button
                      onClick={() => playAudio(line.audioUrl, line.persian, 'dialogue', dialogueIndex)}
                      className="p-2 bg-purple-100 text-purple-700 rounded-full hover:bg-purple-200 transition-colors"
                    >
                      <FaVolumeUp />
                    </button>
                  )}
                </div>
              </div>
            ))}
            
            <div className="grid gap-4 mt-6">
              {scene.patterns.map((pattern: any, patternIndex: number) => (
                <div key={patternIndex} className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 border border-indigo-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <div className="bg-indigo-100 rounded-lg px-4 py-2">
                        <span className="text-lg font-bold text-indigo-800">{pattern.pattern}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span 
                          className="text-2xl font-bold text-gray-900"
                          style={{ fontFamily: 'Vazirmatn, serif' }}
                        >
                          {pattern.example}
                        </span>
                        {pattern.audioUrl && (
                          <button
                            onClick={() => playAudio(pattern.audioUrl, pattern.example, 'pattern', patternIndex)}
                            className="p-2 bg-indigo-100 text-indigo-700 rounded-full hover:bg-indigo-200 transition-colors"
                          >
                            <FaVolumeUp />
                          </button>
                        )}
                      </div>
                    </div>
                    <span className="text-gray-600 italic">({pattern.pronunciation})</span>
                  </div>
                  <p className="text-indigo-700 mb-2">{pattern.description}</p>
                  <div className="text-sm text-indigo-600 bg-indigo-50 rounded-lg p-3">
                    <strong>Breakdown:</strong> {pattern.breakdown}
                  </div>
                </div>
              ))}
            </div>
            
            {scene.culturalNote && (
              <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800">{scene.culturalNote}</p>
              </div>
            )}
            
            <div className="mt-6 text-center">
              <button
                onClick={() => handleSceneComplete(index)}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                  isCompleted
                    ? 'bg-green-200 text-green-900 cursor-default'
                    : 'bg-purple-200 text-purple-900 hover:bg-purple-300 hover:shadow-lg transform hover:scale-105'
                }`}
              >
                {isCompleted ? (
                  <span className="flex items-center justify-center">
                    <FaCheck className="mr-2" />
                    Completed
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <FaCheck className="mr-2" />
                    Mark Complete
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Cultural note scene
    if (scene && typeof scene === 'object' && scene.type === 'cultural-note' && scene.insights) {
      return (
        <div
          key={index}
          className={`bg-white rounded-2xl shadow-lg overflow-hidden border-2 transition-all duration-300 ${
            isActive ? 'border-orange-200' : 'border-gray-200'
          } ${isCompleted ? 'ring-2 ring-green-200' : ''}`}
        >
          <div className="p-6 bg-gradient-to-r from-pink-50 to-rose-50 border-b border-pink-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-black flex items-center">
                🌸 {scene.title || 'Cultural Insights'}
              </h3>
              {isCompleted && (
                <div className="w-8 h-8 bg-green-200 rounded-full flex items-center justify-center">
                  <FaCheck className="text-green-700 text-sm" />
                </div>
              )}
            </div>
          </div>
          <div className="p-6">
            {scene.dialogue && scene.dialogue.map((line: any, dialogueIndex: number) => (
              <div key={dialogueIndex} className="mb-6 bg-pink-50 rounded-lg p-4 border border-pink-100">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <span 
                      className="text-xl font-bold text-gray-900 mr-4"
                      dir="rtl"
                      style={{ fontFamily: 'Vazirmatn, serif' }}
                    >
                      {line.persian}
                    </span>
                    <span className="text-gray-700">{line.english}</span>
                  </div>
                  {line.audioUrl && (
                    <button
                      onClick={() => playAudio(line.audioUrl, line.persian, 'dialogue', dialogueIndex)}
                      className="p-2 bg-pink-100 text-pink-700 rounded-full hover:bg-pink-200 transition-colors"
                    >
                      <FaVolumeUp />
                    </button>
                  )}
                </div>
              </div>
            ))}
            
            <div className="space-y-6">
              {scene.insights.map((insight: any, insightIndex: number) => (
                <div key={insightIndex} className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
                  <h4 className="text-lg font-semibold text-amber-800 mb-3">{insight.title}</h4>
                  <p className="text-amber-700 leading-relaxed">{insight.content}</p>
                </div>
              ))}
            </div>
            
            {scene.practiceAdvice && (
              <div className="mt-6 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-4">
                <p className="text-emerald-800 font-medium">{scene.practiceAdvice}</p>
              </div>
            )}
            
            <div className="mt-6 text-center">
              <button
                onClick={() => handleSceneComplete(index)}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                  isCompleted
                    ? 'bg-green-200 text-green-900 cursor-default'
                    : 'bg-pink-200 text-pink-900 hover:bg-pink-300 hover:shadow-lg transform hover:scale-105'
                }`}
              >
                {isCompleted ? (
                  <span className="flex items-center justify-center">
                    <FaCheck className="mr-2" />
                    Completed
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <FaCheck className="mr-2" />
                    Mark Complete
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Handle scenes with only narration (no dialogue)
    if (scene && typeof scene === 'object' && scene.narration && !scene.dialogue) {
      console.log(`🎭 Rendering narration-only scene ${index}`);
      return (
        <div
          key={index}
          className={`bg-white rounded-2xl shadow-lg overflow-hidden border-2 transition-all duration-300 ${
            isActive ? 'border-orange-200' : 'border-gray-200'
          } ${isCompleted ? 'ring-2 ring-green-200' : ''}`}
        >
          {/* Scene Header */}
          <div className="p-6 bg-gradient-to-r from-orange-50 to-yellow-50 border-b border-orange-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-black">
                {scene.title || `Scene ${index + 1}`}
              </h3>
              {isCompleted && (
                <div className="w-8 h-8 bg-green-200 rounded-full flex items-center justify-center">
                  <FaCheck className="text-green-700 text-sm" />
                </div>
              )}
            </div>
          </div>

          {/* Scene Content */}
          <div className="p-6">
            {/* Narration */}
            <div className="bg-blue-50 rounded-xl p-6 text-lg leading-relaxed text-gray-800 shadow-md">
              <div className="flex items-start">
                <FaBookOpen className="text-blue-600 mr-3 mt-1 flex-shrink-0" />
                <div>{scene.narration}</div>
              </div>
            </div>

            {/* Video */}
            {scene.videoUrl && (
              <div className="mt-6">
                <video 
                  controls 
                  className="w-full rounded-xl shadow-lg"
                  src={scene.videoUrl}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            )}

            {/* Scene Actions */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  if (!isCompleted) {
                    setCompletedScenes(prev => [...prev, index]);
                  } else {
                    setCompletedScenes(prev => prev.filter(i => i !== index));
                  }
                }}
                className={`px-6 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center space-x-2 ${
                  isCompleted 
                    ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                    : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                }`}
              >
                {isCompleted ? (
                  <span className="flex items-center">
                    <FaCheck className="mr-2" />
                    Completed
                  </span>
                ) : (
                  <span className="flex items-center">
                    <FaCheck className="mr-2" />
                    Mark Complete
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // If not a recognized scene type, return null
    console.log(`🎭 Unknown scene structure for scene ${index}:`, scene);
    return null;
  };

  const playAudio = async (audioUrl: string, fallbackText?: string, audioType?: string, index?: number, englishText?: string) => {
    if (!audioUrl) {
      console.warn('No audio URL provided');
      return;
    }
    
    // Log the audio URL being used
    console.log(`🎵 Playing audio: ${audioUrl}`);
    
    // For properly formatted URLs that start with /audio/, use them directly
    let finalAudioUrl = audioUrl;
    
    if (audioUrl.startsWith('/audio/') && audioUrl.includes('.mp3')) {
      console.log('🎵 Using direct audio URL (no mapping needed):', audioUrl);
      finalAudioUrl = audioUrl;
    } else {
      // Get the correct audio URL using the mapper for other cases
      let mappedUrl = audioUrl;
      
      try {
        // Use the audio URL mapper to get the correct URL
        mappedUrl = await getCorrectAudioUrl(pathwayId, audioUrl, lessonId);
        console.log('🎯 Audio URL mapping result:', {
          originalUrl: audioUrl,
          mappedUrl: mappedUrl,
          pathwayId: pathwayId,
          lessonId: lessonId
        });
      } catch (error) {
        console.warn('Audio URL mapping failed, using original URL:', error);
      }
      
      // Use the mapped URL for playback
      finalAudioUrl = mappedUrl || audioUrl;
    }
    
    console.log('🎵 Final audio URL for playback:', finalAudioUrl);
    
    try {
      // Handle ganji-adabi URLs - use browser TTS
      if (finalAudioUrl.includes('/audio/unknown-pathway/unknown-lesson/audio.mp3') || 
          finalAudioUrl.includes('/api/audio/ganji-adabi/')) {
        console.log('🎤 Ganji-adabi URL detected, using browser TTS:', finalAudioUrl);
        await playBrowserTTS(fallbackText || '');
        return;
      }
      
      // Try to play the audio file
      const audio = new Audio(finalAudioUrl);
      
      // Add completion handler
      audio.addEventListener('ended', () => {
        console.log('🎵 Audio playback completed');
      });
      
      // Add error handler
      audio.addEventListener('error', (e) => {
        console.error('🔴 Audio playback error:', e);
        console.log('❌ Audio file not found:', finalAudioUrl);
        if (fallbackText) {
          console.log('🔄 Falling back to browser TTS for:', fallbackText);
          playBrowserTTS(fallbackText);
        }
      });
      
      await audio.play();
      console.log('✅ Audio playback started successfully');
      
    } catch (error) {
      console.error('🔴 Audio playback failed:', error);
      if (fallbackText) {
        console.log('❌ Audio file not found for:', fallbackText);
        // Fall back to browser TTS instead of showing error
        playBrowserTTS(fallbackText);
      }
    }
  };

  // Helper function to play browser TTS
  const playBrowserTTS = async (text: string) => {
    if (!text) {
      console.warn('No text provided for TTS');
      return;
    }
    
    console.log('🎤 Playing browser TTS for:', text);
    
    try {
      // Check if speech synthesis is available
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'fa-IR'; // Persian language
        utterance.rate = 0.8; // Slightly slower for better pronunciation
        utterance.pitch = 1.0;
        utterance.volume = 1.0;
        
        // Get available voices and try to use a Persian voice
        const voices = speechSynthesis.getVoices();
        const persianVoice = voices.find(voice => 
          voice.lang.includes('fa') || 
          voice.lang.includes('ir') || 
          voice.name.toLowerCase().includes('persian') ||
          voice.name.toLowerCase().includes('farsi')
        );
        
        if (persianVoice) {
          utterance.voice = persianVoice;
          console.log('🎤 Using Persian voice:', persianVoice.name);
        } else {
          console.log('🎤 No Persian voice found, using default voice');
        }
        
        speechSynthesis.speak(utterance);
        console.log('✅ Browser TTS started');
      } else {
        console.warn('Speech synthesis not available');
        alert(`Audio not available for: "${text}". Please check back later or contact support.`);
      }
    } catch (error) {
      console.error('❌ Browser TTS failed:', error);
      alert(`Audio not available for: "${text}". Please check back later or contact support.`);
    }
  };

  const renderWordBank = () => {
    // Prefer content.wordBank if available and non-empty
    let wordBank = content?.wordBank && content.wordBank.length > 0 ? content.wordBank : (lesson?.wordBank && lesson.wordBank.length > 0 ? lesson.wordBank : undefined);
    // Fallback: if wordBank is missing or empty, extract from scenes' vocab arrays if present
    if ((!wordBank || wordBank.length === 0) && content?.scenes) {
      wordBank = content.scenes.flatMap(scene => {
        // Only extract vocab if it exists on the scene
        return Array.isArray((scene as any).vocab) ? (scene as any).vocab : [];
      });
    }
    if (!wordBank || wordBank.length === 0) return null;

    return (
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
          <h3 className="text-xl font-bold text-black flex items-center">
            <FaLightbulb className="mr-2 text-green-400" />
            Word Bank
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.isArray(wordBank) ? (
              wordBank.map((word: any, index: number) => {
                const persianWord = word.persian || word.farsi || word.word;
                const englishWord = word.english || word.translation;
                const emoji = getWordEmoji(persianWord, englishWord);
                
                return (
                  <div key={index} className="bg-green-50 rounded-lg p-4 border border-green-100 flex flex-col gap-2">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="text-2xl">{emoji}</div>
                        <div 
                          className="text-lg font-bold text-gray-900"
                          dir="rtl"
                          style={{ textAlign: 'right' }}
                        >
                          {persianWord}
                        </div>
                      </div>
                      <div className="flex space-x-1 rtl:space-x-reverse">
                        {word.audioUrl && word.audioUrl !== '' && (
                          <button
                            onClick={() => playAudio(word.audioUrl, persianWord, 'wordbank', index, englishWord)}
                            className="p-2 bg-green-200 text-green-900 rounded-full hover:bg-green-300 transition-colors"
                            title={`Play audio: ${word.audioUrl}`}
                          >
                            <FaVolumeUp />
                          </button>
                        )}
                      </div>
                    </div>
                  <div 
                    className="text-sm text-gray-600 mb-1"
                    dir={getEnglishTextDirection()}
                    style={{ textAlign: getEnglishTextAlignment() }}
                  >
                    {word.english || word.translation}
                  </div>
                  {word.audioUrl && (
                    <div className="text-xs text-gray-500">
                      <div className="font-mono break-all">
                        {isValidAudioUrl(word.audioUrl) ? (
                          <span className="text-green-600">
                            ✅ {getAudioUrlDisplayText(word.audioUrl)}
                          </span>
                        ) : (
                          <span className="text-orange-600">
                            ⚠️ {word.audioUrl}
                          </span>
                        )}
                      </div>
                      <a
                        href={word.audioUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:underline mt-1 block"
                        dir={getEnglishTextDirection()}
                        style={{ textAlign: getEnglishTextAlignment() }}
                      >
                        Open Audio File
                      </a>
                    </div>
                  )}
                  {(word.pronunciation || word.transliteration) && (
                    <div 
                      className="text-xs text-gray-500 italic"
                      dir={getEnglishTextDirection()}
                      style={{ textAlign: getEnglishTextAlignment() }}
                    >
                      {word.pronunciation || word.transliteration}
                    </div>
                  )}
                </div>
                );
              })
            ) : (
              <div className="col-span-full text-center text-gray-500">
                No vocabulary available.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderActivities = () => {
    if (!content?.activities) return null;

    return (
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="p-6 bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-purple-100">
          <h3 className="text-xl font-bold text-black flex items-center">
            <FaGamepad className="mr-2 text-purple-400" />
            Interactive Activities
          </h3>
          <div className="mt-2 text-sm text-gray-600">
            Score: {getActivityScore()}% ({Object.values(activityResults).filter(r => r).length}/{content.activities.length} correct)
          </div>
        </div>
        <div className="p-6">
          <div className="space-y-6">
            {Array.isArray(content.activities) ? (
              content.activities.map((activity: any, index: number) => (
                <div key={index} className="bg-purple-50 rounded-lg p-6 border border-purple-100">
                  <h4 className="font-semibold text-gray-900 mb-3 text-lg">
                    {activity.type === 'multiple-choice' ? '🎯 Multiple Choice' : 
                     activity.type === 'matching' ? '🎭 Matching' : 
                     activity.type === 'fill-in-the-blank' ? '📝 Fill in the Blank' :
                     activity.type === 'fill-in-blank' ? '📝 Fill in the Blank' :
                     activity.type === 'drag-drop' ? '🎪 Drag & Drop' :
                     activity.type === 'drag-and-drop' ? '🧩 Interactive Drag & Drop' :
                     activity.type === 'interactive-builder' ? '🏗️ Sentence Builder' :
                     activity.type === 'pronunciation-trainer' ? '🎙️ Pronunciation Trainer' :
                     activity.type === 'translation-challenge' ? '🌐 Translation Challenge' :
                     activity.type === 'free-form' ? '✏️ Creative Practice' :
                     activity.type === 'interactive-story' ? '🎬 Interactive Story' :
                     activity.type === 'quiz' ? '🧠 Quiz' :
                     activity.type === 'roleplay' ? '🍽️ Roleplay' :
                     activity.type === 'scene-pick' ? 'Choose the Right Answer' :
                     activity.type === 'video-comprehension' ? 'Video Comprehension' :
                     activity.type === 'speaking-practice' ? 'Speaking Practice' :
                     activity.type === 'cultural-quiz' ? 'Cultural Quiz' :
                     activity.type === 'number-recognition' ? '🔢 Number Recognition' :
                     activity.type === 'cultural-insight' ? '🌍 Cultural Insight' :
                     activity.type === 'pronunciation-practice' ? '🗣️ Pronunciation Practice' :
                     activity.type === 'real-world-application' ? '🌟 Real-World Application' :
                     activity.type === 'dialogue-completion' ? '💬 Dialogue Completion' :
                     activity.type === 'comprehensive-review' ? '📚 Comprehensive Review' :
                     activity.type === 'cultural-note' ? '📖 Cultural Note' :
                     activity.type === 'scenario-practice' ? '🎭 Scenario Practice' :
                     `Activity ${index + 1}`}
                  </h4>
                  {activity.prompt && (
                    <div className="flex items-center justify-between mb-4">
                      <p 
                        className="text-gray-600 font-medium flex-1"
                        dir={getEnglishTextDirection()}
                        style={{ textAlign: getEnglishTextAlignment() }}
                      >
                        {activity.prompt}
                      </p>
                      {activity.audioUrl && (
                        <button
                          onClick={() => playAudio(activity.audioUrl, activity.prompt, 'activity-prompt', index)}
                          className="ml-3 p-2 bg-purple-100 text-purple-600 rounded-full hover:bg-purple-200 transition-colors"
                          title={`Play audio for activity prompt`}
                        >
                          <FaVolumeUp />
                        </button>
                      )}
                    </div>
                  )}
                  {activity.question && (
                    <p 
                      className="text-gray-600 mb-4 font-medium"
                      dir={getEnglishTextDirection()}
                      style={{ textAlign: getEnglishTextAlignment() }}
                    >
                      {activity.question}
                    </p>
                  )}

                  {/* Fill in the Blank Activity */}
                  {activity.type === 'fill-in-the-blank' && (
                    <div className="space-y-4">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Type your answer in Farsi..."
                          value={activityAnswers[index] || ''}
                          onChange={(e) => setActivityAnswers(prev => ({
                            ...prev,
                            [index]: e.target.value
                          }))}
                          disabled={showActivityResults[index]}
                          className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:border-purple-500 focus:outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm disabled:opacity-50"
                          dir="rtl"
                          style={{ textAlign: 'right' }}
                        />
                        <button
                          onClick={() => toggleKeyboard(index)}
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 p-2 text-purple-600 hover:text-purple-800 transition-colors"
                          title="Toggle Farsi Keyboard"
                        >
                          <FaKeyboard />
                        </button>
                      </div>

                      {/* Farsi Keyboard */}
                      {showKeyboard[index] && (
                        <div className="mt-4 border rounded-lg overflow-hidden">
                          <FarsiKeyboard
                            onInputChange={(text) => setActivityAnswers(prev => ({
                              ...prev,
                              [index]: text
                            }))}
                            currentText={activityAnswers[index] || ''}
                            placeholder="Type your answer in Farsi..."
                          />
                        </div>
                      )}
                      
                      <div className="flex space-x-3">
                        {!showActivityResults[index] && (
                          <button
                            onClick={() => checkActivityResult(index, activity)}
                            disabled={!activityAnswers[index]}
                            className="px-6 py-3 bg-purple-100 text-purple-900 rounded-lg hover:bg-purple-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Check Answer
                          </button>
                        )}
                        <button
                          onClick={() => toggleKeyboard(index)}
                          className={`px-4 py-3 rounded-lg transition-colors flex items-center ${
                            showKeyboard[index] 
                              ? 'bg-purple-600 text-white hover:bg-purple-700' 
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          <FaKeyboard className="mr-2" />
                          {showKeyboard[index] ? 'Hide Keyboard' : 'Show Farsi Keyboard'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Syllable Breakdown Activity */}
                  {activity.type === 'syllable-breakdown' && activity.words && (
                    <div className="space-y-6">
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="text-lg font-semibold text-blue-800 mb-2 flex items-center">
                          🎯 {activity.title || 'Syllable Breakdown Challenge'}
                        </h4>
                        <p className="text-blue-700">{activity.instructions}</p>
                      </div>
                      
                      {activity.words.map((wordItem: any, wordIndex: number) => (
                        <div key={wordIndex} className="bg-white border-2 border-gray-200 rounded-xl p-6">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-4">
                              <span 
                                className="text-3xl font-bold text-gray-900 font-vazir"
                                dir="rtl"
                                style={{ fontFamily: 'Vazirmatn, serif' }}
                              >
                                {wordItem.word}
                              </span>
                              {wordItem.audioUrl && (
                                <button
                                  onClick={() => playAudio(wordItem.audioUrl, wordItem.word, 'syllable-practice', wordIndex)}
                                  className="p-3 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
                                  title="Play pronunciation"
                                >
                                  <FaVolumeUp className="text-lg" />
                                </button>
                              )}
                            </div>
                            <span className="text-gray-600 italic">{wordItem.translation}</span>
                          </div>
                          
                          {/* Interactive syllable breakdown */}
                          <div className="bg-gray-50 rounded-lg p-4 mb-4">
                            <p className="text-sm font-medium text-gray-700 mb-3">Break this word into syllables:</p>
                            <div className="flex items-center space-x-2 flex-wrap">
                              {wordItem.word.split('').map((char: string, charIndex: number) => (
                                <div key={charIndex} className="flex items-center">
                                  <span 
                                    className="text-2xl font-bold text-gray-800 px-2 py-1"
                                    style={{ fontFamily: 'Vazirmatn, serif' }}
                                  >
                                    {char}
                                  </span>
                                  {charIndex < wordItem.word.length - 1 && (
                                    <button
                                      onClick={() => {
                                        // Toggle syllable break
                                        const key = `syllable-break-${index}-${wordIndex}-${charIndex}`;
                                        setActivityAnswers(prev => ({
                                          ...prev,
                                          [key]: !prev[key]
                                        }));
                                      }}
                                      className={`w-8 h-8 rounded-full border-2 mx-1 flex items-center justify-center transition-all ${
                                        activityAnswers[`syllable-break-${index}-${wordIndex}-${charIndex}`]
                                          ? 'bg-orange-200 border-orange-400 text-orange-800'
                                          : 'bg-gray-200 border-gray-300 text-gray-500 hover:bg-orange-100'
                                      }`}
                                      title="Click to add/remove syllable break"
                                    >
                                      |
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          {/* Show correct breakdown when answered */}
                          {activityAnswers[`syllable-result-${index}-${wordIndex}`] && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                              <p className="text-green-800 font-medium mb-2">Correct breakdown:</p>
                              <div className="flex items-center space-x-2">
                                {wordItem.correctBreakdown.map((syllable: string, sylIndex: number) => (
                                  <div key={sylIndex} className="flex items-center">
                                    <span 
                                      className="text-xl font-bold text-green-700 bg-green-100 px-3 py-1 rounded-lg"
                                      style={{ fontFamily: 'Vazirmatn, serif' }}
                                    >
                                      {syllable}
                                    </span>
                                    {sylIndex < wordItem.correctBreakdown.length - 1 && (
                                      <span className="text-green-600 mx-2">-</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          <button
                            onClick={() => {
                              setActivityAnswers(prev => ({
                                ...prev,
                                [`syllable-result-${index}-${wordIndex}`]: true
                              }));
                            }}
                            className="mt-4 px-6 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                          >
                            Show Correct Answer
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Syllable Counting Activity */}
                  {activity.type === 'syllable-counting' && activity.questions && (
                    <div className="space-y-6">
                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
                        <h4 className="text-lg font-semibold text-purple-800 mb-2 flex items-center">
                          🔢 Count the Syllables
                        </h4>
                        <p className="text-purple-700">Listen carefully and count how many syllables each word has.</p>
                      </div>
                      
                      {activity.questions.map((question: any, qIndex: number) => (
                        <div key={qIndex} className="bg-white border-2 border-gray-200 rounded-xl p-6">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-4">
                              <span 
                                className="text-3xl font-bold text-gray-900"
                                dir="rtl"
                                style={{ fontFamily: 'Vazirmatn, serif' }}
                              >
                                {question.word}
                              </span>
                              {question.audioUrl && (
                                <button
                                  onClick={() => playAudio(question.audioUrl, question.word, 'syllable-counting', qIndex)}
                                  className="p-3 bg-purple-100 text-purple-700 rounded-full hover:bg-purple-200 transition-colors"
                                  title="Play pronunciation"
                                >
                                  <FaVolumeUp className="text-lg" />
                                </button>
                              )}
                            </div>
                            <span className="text-gray-600 italic">({question.translation})</span>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                            {question.options.map((option: string, optIndex: number) => (
                              <button
                                key={optIndex}
                                onClick={() => {
                                  setActivityAnswers(prev => ({
                                    ...prev,
                                    [`counting-${index}-${qIndex}`]: option
                                  }));
                                }}
                                className={`p-3 rounded-lg border-2 text-center transition-all ${
                                  activityAnswers[`counting-${index}-${qIndex}`] === option
                                    ? 'border-purple-300 bg-purple-100 text-purple-800'
                                    : 'border-gray-200 hover:border-purple-200 text-gray-700'
                                }`}
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                          
                          {activityAnswers[`counting-${index}-${qIndex}`] && (
                            <div className={`p-4 rounded-lg ${
                              activityAnswers[`counting-${index}-${qIndex}`] === question.answer
                                ? 'bg-green-50 border border-green-200'
                                : 'bg-red-50 border border-red-200'
                            }`}>
                              <p className={`font-medium ${
                                activityAnswers[`counting-${index}-${qIndex}`] === question.answer
                                  ? 'text-green-800'
                                  : 'text-red-800'
                              }`}>
                                {activityAnswers[`counting-${index}-${qIndex}`] === question.answer
                                  ? '✅ Correct!'
                                  : `❌ Incorrect. ${question.explanation}`
                                }
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Pattern Matching Activity */}
                  {activity.type === 'pattern-matching' && activity.matchPairs && (
                    <div className="space-y-6">
                      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4">
                        <h4 className="text-lg font-semibold text-yellow-800 mb-2 flex items-center">
                          🔄 Pattern Recognition
                        </h4>
                        <p className="text-yellow-700">Match each syllable with its pattern type.</p>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Left side - Syllables */}
                        <div className="space-y-3">
                          <h5 className="font-semibold text-gray-800 mb-3">Syllables:</h5>
                          {activity.matchPairs.map((pair: any, pairIndex: number) => (
                            <button
                              key={`left-${pairIndex}`}
                              onClick={() => {
                                if (firstSelection[index]?.type === 'left' && firstSelection[index]?.value === pair.left) {
                                  // Deselect if clicking same item
                                  setFirstSelection(prev => ({ ...prev, [index]: null }));
                                } else {
                                  setFirstSelection(prev => ({ 
                                    ...prev, 
                                    [index]: { type: 'left', value: pair.left }
                                  }));
                                }
                              }}
                              className={`w-full p-4 rounded-xl border-2 text-center transition-all ${
                                firstSelection[index]?.type === 'left' && firstSelection[index]?.value === pair.left
                                  ? 'border-orange-300 bg-orange-100 text-orange-800'
                                  : 'border-gray-200 hover:border-orange-200 bg-white'
                              } ${
                                matchedPairs[index]?.some((matched: any) => matched.left === pair.left)
                                  ? 'border-green-300 bg-green-100 text-green-800'
                                  : ''
                              }`}
                              style={{ fontFamily: 'Vazirmatn, serif' }}
                            >
                              <span className="text-2xl font-bold">{pair.left}</span>
                            </button>
                          ))}
                        </div>
                        
                        {/* Right side - Patterns */}
                        <div className="space-y-3">
                          <h5 className="font-semibold text-gray-800 mb-3">Patterns:</h5>
                          {activity.matchPairs.map((pair: any, pairIndex: number) => (
                            <button
                              key={`right-${pairIndex}`}
                              onClick={() => {
                                if (firstSelection[index]?.type === 'right' && firstSelection[index]?.value === pair.right) {
                                  // Deselect if clicking same item
                                  setFirstSelection(prev => ({ ...prev, [index]: null }));
                                } else if (firstSelection[index]?.type === 'left') {
                                  // Complete the match
                                  const leftValue = firstSelection[index]?.value;
                                  const isCorrect = activity.matchPairs.some((p: any) => p.left === leftValue && p.right === pair.right);
                                  
                                  if (isCorrect) {
                                    setMatchedPairs(prev => ({
                                      ...prev,
                                      [index]: [...(prev[index] || []), { left: leftValue, right: pair.right }]
                                    }));
                                  } else {
                                    setIncorrectMatches(prev => ({
                                      ...prev,
                                      [index]: [...(prev[index] || []), { left: leftValue, right: pair.right }]
                                    }));
                                    // Clear incorrect match after 2 seconds
                                    setTimeout(() => {
                                      setIncorrectMatches(prev => ({
                                        ...prev,
                                        [index]: prev[index]?.filter((match: any) => 
                                          !(match.left === leftValue && match.right === pair.right)
                                        ) || []
                                      }));
                                    }, 2000);
                                  }
                                  
                                  setFirstSelection(prev => ({ ...prev, [index]: null }));
                                } else {
                                  setFirstSelection(prev => ({ 
                                    ...prev, 
                                    [index]: { type: 'right', value: pair.right }
                                  }));
                                }
                              }}
                              className={`w-full p-4 rounded-xl border-2 text-center transition-all ${
                                firstSelection[index]?.type === 'right' && firstSelection[index]?.value === pair.right
                                  ? 'border-orange-300 bg-orange-100 text-orange-800'
                                  : 'border-gray-200 hover:border-orange-200 bg-white'
                              } ${
                                matchedPairs[index]?.some((matched: any) => matched.right === pair.right)
                                  ? 'border-green-300 bg-green-100 text-green-800'
                                  : ''
                              } ${
                                incorrectMatches[index]?.some((incorrect: any) => incorrect.right === pair.right)
                                  ? 'border-red-300 bg-red-100 text-red-800'
                                  : ''
                              }`}
                            >
                              <div className="text-lg font-semibold">{pair.right}</div>
                              {pair.explanation && matchedPairs[index]?.some((matched: any) => matched.right === pair.right) && (
                                <div className="text-sm text-green-600 mt-2">{pair.explanation}</div>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Progress indicator */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">
                            Progress: {matchedPairs[index]?.length || 0} / {activity.matchPairs.length} matched
                          </span>
                          {matchedPairs[index]?.length === activity.matchPairs.length && (
                            <span className="text-green-600 font-semibold">🎉 Complete!</span>
                          )}
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full transition-all duration-300"
                            style={{ 
                              width: `${((matchedPairs[index]?.length || 0) / activity.matchPairs.length) * 100}%` 
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Multiple Choice Activity */}
                  {activity.type === 'multiple-choice' && activity.options && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {activity.options && activity.options.map((option: string, optionIndex: number) => (
                          <button
                            key={optionIndex}
                            onClick={() => handleMultipleChoiceAnswer(index, option)}
                            disabled={showActivityResults[index]}
                            className={`p-4 rounded-lg border-2 text-left transition-all duration-200 ${
                              activityAnswers[index] === option
                                ? 'border-purple-200 bg-purple-50'
                                : 'border-gray-200 hover:border-purple-100'
                            } ${
                              showActivityResults[index] && activity.correctAnswer === option
                                ? 'border-green-200 bg-green-50'
                                : ''
                            } ${
                              showActivityResults[index] && activityAnswers[index] === option && activity.correctAnswer !== option
                                ? 'border-red-200 bg-red-50'
                                : ''
                            }`}
                          >
                            <div className="flex items-center">
                              <div className={`w-6 h-6 rounded-full border-2 mr-3 flex items-center justify-center ${
                                activityAnswers[index] === option
                                  ? 'border-purple-200 bg-purple-200 text-purple-900'
                                  : 'border-gray-200'
                              }`}>
                                {activityAnswers[index] === option && '✓'}
                              </div>
                              <span 
                                className="font-medium text-gray-900"
                                dir={getEnglishTextDirection()}
                                style={{ textAlign: getEnglishTextAlignment() }}
                              >
                                {option}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                      
                      {!showActivityResults[index] && (
                        <button
                          onClick={() => checkActivityResult(index, activity)}
                          disabled={!activityAnswers[index]}
                          className="px-6 py-3 bg-purple-100 text-purple-900 rounded-lg hover:bg-purple-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Check Answer
                        </button>
                      )}
                    </div>
                  )}

                  {/* Enhanced Drag and Drop Activity */}
                  {activity.type === 'drag-and-drop' && activity.items && (
                    <div className="space-y-4">
                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-4">
                        <p className="text-blue-700 text-sm font-medium mb-2">
                          🧩 Drag the correct suffix to complete each sentence
                        </p>
                      </div>
                      {activity.items.map((item: any, itemIndex: number) => (
                        <div key={itemIndex} className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-lg font-medium" dir="rtl" style={{fontFamily: 'Vazirmatn, serif'}}>
                              {item.sentence}
                            </p>
                            <span className="text-sm text-gray-500">
                              ({item.translation})
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {item.options && item.options.map((option: string, optionIndex: number) => (
                              <button
                                key={optionIndex}
                                className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full hover:bg-purple-200 transition-colors text-sm"
                                style={{fontFamily: 'Vazirmatn, serif'}}
                                onClick={() => {
                                  // Handle drag and drop selection
                                  const newAnswers = {...activityAnswers};
                                  newAnswers[`${index}-${itemIndex}`] = option;
                                  setActivityAnswers(newAnswers);
                                }}
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                          {activityAnswers[`${index}-${itemIndex}`] && (
                            <div className={`mt-2 p-2 rounded-lg text-sm ${
                              activityAnswers[`${index}-${itemIndex}`] === item.answer 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {activityAnswers[`${index}-${itemIndex}`] === item.answer 
                                ? '✅ Correct!' 
                                : `❌ Try again. The correct answer is: ${item.answer}`}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Interactive Sentence Builder */}
                  {activity.type === 'interactive-builder' && activity.components && (
                    <div className="space-y-6">
                      <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                        <p className="text-green-700 text-sm font-medium mb-2">
                          🏗️ Build your own Persian sentences! Choose components below:
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Pronouns */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <h5 className="font-medium text-gray-800 mb-3">1. Choose a pronoun:</h5>
                          <div className="space-y-2">
                            {activity.components.pronouns && activity.components.pronouns.map((pronoun: any, pIndex: number) => (
                              <button
                                key={pIndex}
                                className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                                  activityAnswers[`builder-${index}-pronoun`] === pronoun.persian
                                    ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                    : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
                                }`}
                                onClick={() => {
                                  const newAnswers = {...activityAnswers};
                                  newAnswers[`builder-${index}-pronoun`] = pronoun.persian;
                                  setActivityAnswers(newAnswers);
                                }}
                              >
                                <div dir="rtl" style={{fontFamily: 'Vazirmatn, serif'}} className="font-medium">
                                  {pronoun.persian}
                                </div>
                                <div className="text-xs text-gray-600">{pronoun.english}</div>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Words */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <h5 className="font-medium text-gray-800 mb-3">2. Choose a word:</h5>
                          <div className="space-y-2">
                            {activity.components.words && activity.components.words.map((word: any, wIndex: number) => (
                              <button
                                key={wIndex}
                                className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                                  activityAnswers[`builder-${index}-word`] === word.persian
                                    ? 'bg-green-100 text-green-700 border border-green-300'
                                    : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
                                }`}
                                onClick={() => {
                                  const newAnswers = {...activityAnswers};
                                  newAnswers[`builder-${index}-word`] = word.persian;
                                  setActivityAnswers(newAnswers);
                                }}
                              >
                                <div dir="rtl" style={{fontFamily: 'Vazirmatn, serif'}} className="font-medium">
                                  {word.persian}
                                </div>
                                <div className="text-xs text-gray-600">{word.english}</div>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Suffixes */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <h5 className="font-medium text-gray-800 mb-3">3. Choose a suffix:</h5>
                          <div className="space-y-2">
                            {activity.components.suffixes && activity.components.suffixes.map((suffix: any, sIndex: number) => (
                              <button
                                key={sIndex}
                                className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                                  activityAnswers[`builder-${index}-suffix`] === suffix.persian
                                    ? 'bg-purple-100 text-purple-700 border border-purple-300'
                                    : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
                                }`}
                                onClick={() => {
                                  const newAnswers = {...activityAnswers};
                                  newAnswers[`builder-${index}-suffix`] = suffix.persian;
                                  setActivityAnswers(newAnswers);
                                }}
                              >
                                <div dir="rtl" style={{fontFamily: 'Vazirmatn, serif'}} className="font-medium">
                                  {suffix.persian}
                                </div>
                                <div className="text-xs text-gray-600">{suffix.english} ({suffix.usage})</div>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Built Sentence Display */}
                      {(activityAnswers[`builder-${index}-pronoun`] || activityAnswers[`builder-${index}-word`] || activityAnswers[`builder-${index}-suffix`]) && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <h5 className="font-medium text-gray-800 mb-2">Your sentence:</h5>
                          <div className="text-xl font-bold mb-2" dir="rtl" style={{fontFamily: 'Vazirmatn, serif'}}>
                            {activityAnswers[`builder-${index}-pronoun`] || '___'} {activityAnswers[`builder-${index}-word`] || '___'}{activityAnswers[`builder-${index}-suffix`] || '___'}
                          </div>
                          {activityAnswers[`builder-${index}-pronoun`] && activityAnswers[`builder-${index}-word`] && activityAnswers[`builder-${index}-suffix`] && (
                            <div className="text-sm text-gray-600">
                              Great! You've built a complete sentence! 🎉
                            </div>
                          )}
                        </div>
                      )}

                      {/* Examples */}
                      {activity.examples && (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <h5 className="font-medium text-gray-800 mb-3">💡 Example sentences:</h5>
                          <div className="space-y-2">
                            {activity.examples.map((example: any, exIndex: number) => (
                              <div key={exIndex} className="flex items-center justify-between">
                                <div>
                                  <div dir="rtl" style={{fontFamily: 'Vazirmatn, serif'}} className="font-medium">
                                    {example.persian}
                                  </div>
                                  <div className="text-sm text-gray-600">{example.english}</div>
                                </div>
                                {example.audioUrl && (
                                  <button
                                    onClick={() => playAudio(example.audioUrl, example.persian, 'example', exIndex)}
                                    className="ml-3 p-2 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200 transition-colors"
                                  >
                                    <FaVolumeUp />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Pronunciation Trainer */}
                  {activity.type === 'pronunciation-trainer' && activity.phrases && (
                    <div className="space-y-4">
                      <div className="bg-orange-50 border border-orange-100 rounded-lg p-4">
                        <p className="text-orange-700 text-sm font-medium">
                          🎙️ Practice pronunciation with these phrases. Listen carefully and try to repeat:
                        </p>
                      </div>
                      {activity.phrases.map((phrase: any, phraseIndex: number) => (
                        <div key={phraseIndex} className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex-1">
                              <div dir="rtl" style={{fontFamily: 'Vazirmatn, serif'}} className="text-lg font-medium mb-1">
                                {phrase.persian}
                              </div>
                              <div className="text-gray-600 mb-1">{phrase.english}</div>
                              <div className="text-sm text-blue-600 italic">Phonetic: {phrase.phonetic}</div>
                            </div>
                            {phrase.audioUrl && (
                              <button
                                onClick={() => playAudio(phrase.audioUrl, phrase.persian, 'pronunciation', phraseIndex)}
                                className="ml-3 p-3 bg-orange-100 text-orange-600 rounded-full hover:bg-orange-200 transition-colors"
                              >
                                <FaVolumeUp className="text-lg" />
                              </button>
                            )}
                          </div>
                          {phrase.tips && (
                            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                              <h6 className="font-medium text-blue-800 mb-2">💡 Pronunciation Tips:</h6>
                              <ul className="text-sm text-blue-700 space-y-1">
                                {phrase.tips.map((tip: string, tipIndex: number) => (
                                  <li key={tipIndex}>• {tip}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Translation Challenge */}
                  {activity.type === 'translation-challenge' && activity.challenges && (
                    <div className="space-y-4">
                      <div className="bg-teal-50 border border-teal-100 rounded-lg p-4">
                        <p className="text-teal-700 text-sm font-medium">
                          🌐 Translate these sentences to Persian. Use the hints if you need help!
                        </p>
                      </div>
                      {activity.challenges.map((challenge: any, challengeIndex: number) => (
                        <div key={challengeIndex} className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="mb-3">
                            <div className="text-lg font-medium mb-2">Translate: "{challenge.english}"</div>
                            {challenge.hints && (
                              <details className="mb-3">
                                <summary className="text-sm text-blue-600 cursor-pointer">💡 Show hints</summary>
                                <ul className="mt-2 text-sm text-blue-700 bg-blue-50 p-3 rounded-lg">
                                  {challenge.hints.map((hint: string, hintIndex: number) => (
                                    <li key={hintIndex}>• {hint}</li>
                                  ))}
                                </ul>
                              </details>
                            )}
                          </div>
                          <input
                            type="text"
                            className="w-full p-3 border border-gray-300 rounded-lg"
                            style={{fontFamily: 'Vazirmatn, serif'}}
                            dir="rtl"
                            placeholder="Type your translation here..."
                            value={activityAnswers[`challenge-${index}-${challengeIndex}`] || ''}
                            onChange={(e) => {
                              const newAnswers = {...activityAnswers};
                              newAnswers[`challenge-${index}-${challengeIndex}`] = e.target.value;
                              setActivityAnswers(newAnswers);
                            }}
                          />
                          {activityAnswers[`challenge-${index}-${challengeIndex}`] && (
                            <div className="mt-3">
                              <button
                                onClick={() => {
                                  const userAnswer = activityAnswers[`challenge-${index}-${challengeIndex}`];
                                  const isCorrect = userAnswer === challenge.answer;
                                  const newResults = {...activityResults};
                                  newResults[`challenge-${index}-${challengeIndex}`] = isCorrect;
                                  setActivityResults(newResults);
                                }}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                              >
                                Check Answer
                              </button>
                              {activityResults[`challenge-${index}-${challengeIndex}`] !== undefined && (
                                <div className={`mt-2 p-3 rounded-lg ${
                                  activityResults[`challenge-${index}-${challengeIndex}`]
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-red-100 text-red-700'
                                }`}>
                                  {activityResults[`challenge-${index}-${challengeIndex}`] ? (
                                    <div>
                                      ✅ Excellent! Correct answer: {challenge.answer}
                                      {challenge.audioUrl && (
                                        <button
                                          onClick={() => playAudio(challenge.audioUrl, challenge.answer, 'challenge-audio', challengeIndex)}
                                          className="ml-2 p-1 bg-green-200 text-green-700 rounded-full hover:bg-green-300 transition-colors"
                                        >
                                          <FaVolumeUp className="text-sm" />
                                        </button>
                                      )}
                                    </div>
                                  ) : (
                                    <div>❌ Not quite right. The correct answer is: {challenge.answer}</div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Free-form Creative Practice */}
                  {activity.type === 'free-form' && (
                    <div className="space-y-4">
                      <div className="bg-pink-50 border border-pink-100 rounded-lg p-4">
                        <p className="text-pink-700 text-sm font-medium">
                          ✏️ Get creative! Use the word and suffix banks to create your own sentences.
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {activity.wordBank && (
                          <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <h5 className="font-medium text-gray-800 mb-3">Word Bank:</h5>
                            <div className="flex flex-wrap gap-2">
                              {activity.wordBank.map((word: string, wordIndex: number) => (
                                <span
                                  key={wordIndex}
                                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                                  style={{fontFamily: 'Vazirmatn, serif'}}
                                >
                                  {word}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {activity.suffixBank && (
                          <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <h5 className="font-medium text-gray-800 mb-3">Suffix Bank:</h5>
                            <div className="flex flex-wrap gap-2">
                              {activity.suffixBank.map((suffix: string, suffixIndex: number) => (
                                <span
                                  key={suffixIndex}
                                  className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                                  style={{fontFamily: 'Vazirmatn, serif'}}
                                >
                                  {suffix}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <h5 className="font-medium text-gray-800 mb-3">Your creative sentences:</h5>
                        <textarea
                          className="w-full p-3 border border-gray-300 rounded-lg h-32"
                          style={{fontFamily: 'Vazirmatn, serif'}}
                          dir="rtl"
                          placeholder="Write your own sentences here using the words and suffixes above..."
                          value={activityAnswers[`freeform-${index}`] || ''}
                          onChange={(e) => {
                            const newAnswers = {...activityAnswers};
                            newAnswers[`freeform-${index}`] = e.target.value;
                            setActivityAnswers(newAnswers);
                          }}
                        />
                        {activity.examples && (
                          <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
                            <h6 className="font-medium text-gray-700 mb-2">💡 Examples to inspire you:</h6>
                            <ul className="text-sm text-gray-600 space-y-1">
                              {activity.examples.map((example: string, exampleIndex: number) => (
                                <li key={exampleIndex} className="text-gray-600">{example}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Interactive Matching Activity */}
                  {activity.type === 'matching' && (
                    <div className="space-y-6">
                      {/* Instructions */}
                      <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
                        <p className="text-blue-700 text-sm">
                          <strong>How to play:</strong> Click on a Persian phrase first, then click on its English meaning. 
                          If you're correct, the pair will stay matched. If wrong, try again!
                        </p>
                      </div>

                      {/* Progress Indicator */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">
                          Matched: {(matchedPairs[index] || []).length} / {
                            (() => {
                              if (activity.matchPairs || activity.pairs) {
                                return (activity.matchPairs || activity.pairs).length;
                              } else if (activity.correctMatches) {
                                return activity.correctMatches.length;
                              }
                              return 0;
                            })()
                          }
                        </span>
                        {firstSelection[index] && (
                          <span className="text-sm text-purple-600 font-medium">
                            Selected: {firstSelection[index]?.value}
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Persian Column */}
                        <div>
                          <h5 className="font-semibold text-gray-900 mb-3">Persian Phrases</h5>
                          <div className="space-y-2">
                            {(() => {
                              let pairs = [];
                              if (activity.matchPairs || activity.pairs) {
                                pairs = activity.matchPairs || activity.pairs || [];
                              } else if (activity.correctMatches) {
                                pairs = activity.correctMatches || [];
                              }
                              return pairs;
                            })().map((pair: any, itemIndex: number) => {
                              const persianText = pair.persian || pair.left;
                              const isMatched = matchedPairs[index]?.some(match => match.persian === persianText);
                              const isIncorrect = incorrectMatches[index]?.some(match => match.persian === persianText);
                              const isSelected = firstSelection[index]?.type === 'persian' && firstSelection[index]?.value === persianText;
                              
                              return (
                                <div
                                  key={itemIndex}
                                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                                    isMatched 
                                      ? 'border-green-500 bg-green-100 dark:bg-green-800 opacity-60 cursor-not-allowed'
                                      : isIncorrect
                                      ? 'border-red-500 bg-red-100 dark:bg-red-800 animate-pulse'
                                      : isSelected
                                      ? 'border-purple-500 bg-purple-100 dark:bg-purple-800'
                                      : 'border-gray-300 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-600'
                                  }`}
                                  onClick={() => !isMatched && handleMatchingSelection(index, 'persian', persianText)}
                                >
                                  <div className="flex items-center justify-between">
                                    <div 
                                      className="font-medium text-gray-900 flex-1"
                                      dir="rtl"
                                      style={{ textAlign: 'right' }}
                                    >
                                      {persianText}
                                    </div>
                                    {pair.audioUrl && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          playAudio(pair.audioUrl, persianText, 'matching-pair', itemIndex);
                                        }}
                                        className="ml-2 p-1 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200 transition-colors"
                                        title={`Play audio for ${persianText}`}
                                      >
                                        <FaVolumeUp className="text-xs" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* English Column */}
                        <div>
                          <h5 className="font-semibold text-gray-900 mb-3">English Meanings</h5>
                          <div className="space-y-2">
                            {(() => {
                              let pairs = [];
                              if (activity.matchPairs || activity.pairs) {
                                pairs = activity.matchPairs || activity.pairs || [];
                              } else if (activity.correctMatches) {
                                pairs = activity.correctMatches || [];
                              }
                              return pairs;
                            })().map((pair: any, itemIndex: number) => {
                              const englishText = pair.english || pair.right;
                              const isMatched = matchedPairs[index]?.some(match => match.english === englishText);
                              const isIncorrect = incorrectMatches[index]?.some(match => match.english === englishText);
                              const isSelected = firstSelection[index]?.type === 'english' && firstSelection[index]?.value === englishText;
                              
                              return (
                                <div
                                  key={itemIndex}
                                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                                    isMatched 
                                      ? 'border-green-500 bg-green-100 dark:bg-green-800 opacity-60 cursor-not-allowed'
                                      : isIncorrect
                                      ? 'border-red-500 bg-red-100 dark:bg-red-800 animate-pulse'
                                      : isSelected
                                      ? 'border-purple-500 bg-purple-100 dark:bg-purple-800'
                                      : 'border-gray-300 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-600'
                                  }`}
                                  onClick={() => !isMatched && handleMatchingSelection(index, 'english', englishText)}
                                >
                                  <div 
                                    className="text-gray-900 dark:text-gray-100"
                                    dir={getEnglishTextDirection()}
                                    style={{ textAlign: getEnglishTextAlignment() }}
                                  >
                                    {englishText}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex space-x-3">
                        {!showActivityResults[index] && (
                          <>
                            <button
                              onClick={() => checkActivityResult(index, activity)}
                              disabled={(matchedPairs[index] || []).length < (() => {
                                if (activity.matchPairs || activity.pairs) {
                                  return (activity.matchPairs || activity.pairs).length;
                                } else if (activity.correctMatches) {
                                  return activity.correctMatches.length;
                                }
                                return 0;
                              })()}
                              className="px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Check All Matches
                            </button>
                            <button
                              onClick={() => resetMatchingActivity(index)}
                              className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                            >
                              Reset All
                            </button>
                          </>
                        )}
                      </div>

                      {/* Matched Pairs Display */}
                      {(matchedPairs[index] || []).length > 0 && (
                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
                          <h6 className="font-semibold text-green-800 dark:text-green-200 mb-2">Correct Matches:</h6>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {matchedPairs[index]?.map((match, matchIndex) => (
                              <div key={matchIndex} className="flex items-center space-x-2 text-sm">
                                <span className="text-green-600 dark:text-green-400">✓</span>
                                <span 
                                  className="font-medium text-gray-900 dark:text-white"
                                  dir="rtl"
                                  style={{ textAlign: 'right' }}
                                >
                                  {match.persian}
                                </span>
                                <span className="text-gray-500">→</span>
                                <span 
                                  className="text-gray-700 dark:text-gray-300"
                                  dir={getEnglishTextDirection()}
                                  style={{ textAlign: getEnglishTextAlignment() }}
                                >
                                  {match.english}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Video Comprehension Activity */}
                  {activity.type === 'video-comprehension' && activity.options && activity.prompt && (
                    <div className="space-y-3">
                      <div className="bg-gray-50 border border-gray-100 rounded-lg p-4 mb-4">
                        <p className="text-blue-700 text-sm">
                          <strong>Video Comprehension:</strong> Watch the video carefully and answer the question below.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {activity.options.map((option: string, optionIndex: number) => (
                          <button
                            key={optionIndex}
                            onClick={() => handleMultipleChoiceAnswer(index, option)}
                            disabled={showActivityResults[index]}
                            className={`p-4 rounded-lg border-2 text-left transition-all duration-200 ${
                              activityAnswers[index] === option
                                ? 'border-purple-200 bg-purple-50'
                                : 'border-gray-200 hover:border-purple-100'
                            } ${
                              showActivityResults[index] && activity.answer === option
                                ? 'border-green-200 bg-green-50'
                                : ''
                            } ${
                              showActivityResults[index] && activityAnswers[index] === option && activity.answer !== option
                                ? 'border-red-200 bg-red-50'
                                : ''
                            }`}
                          >
                            <div className="flex items-center">
                              <div className={`w-6 h-6 rounded-full border-2 mr-3 flex items-center justify-center ${
                                activityAnswers[index] === option
                                  ? 'border-purple-200 bg-purple-200 text-purple-900'
                                  : 'border-gray-200'
                              }`}>
                                {activityAnswers[index] === option && '✓'}
                              </div>
                              <span 
                                className="font-medium text-gray-900"
                                dir={getEnglishTextDirection()}
                                style={{ textAlign: getEnglishTextAlignment() }}
                              >
                                {option}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Speaking Practice Activity */}
                  {activity.type === 'speaking-practice' && activity.prompt && (
                    <div className="space-y-4">
                      <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                        <p className="text-green-700 text-sm">
                          <strong>Speaking Practice:</strong> {activity.explanation || 'Practice pronouncing the phrase below.'}
                        </p>
                      </div>
                      <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                        <div 
                          className="text-2xl font-bold text-gray-900 mb-2"
                          dir="rtl"
                          style={{ textAlign: 'right' }}
                        >
                          {activity.prompt || 'Practice this phrase'}
                        </div>
                        <div 
                          className="text-gray-600 text-sm mb-4"
                          dir={getEnglishTextDirection()}
                          style={{ textAlign: getEnglishTextAlignment() }}
                        >
                          {activity.prompt && activity.prompt.includes('سلام بچه‌ها') ? 'Hello children! Welcome' : 'Practice this phrase'}
                        </div>
                        <button
                          onClick={() => playAudio(activity.audioUrl)}
                          className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                        >
                          <FaVolumeUp className="inline mr-2" />
                          Listen & Repeat
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Cultural Quiz Activity */}
                  {activity.type === 'cultural-quiz' && activity.options && activity.prompt && (
                    <div className="space-y-3">
                      <div className="bg-orange-50 border border-orange-100 rounded-lg p-4 mb-4">
                        <p className="text-orange-700 text-sm">
                          <strong>Cultural Quiz:</strong> Test your knowledge about Persian culture and traditions.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {activity.options.map((option: string, optionIndex: number) => (
                          <button
                            key={optionIndex}
                            onClick={() => handleMultipleChoiceAnswer(index, option)}
                            disabled={showActivityResults[index]}
                            className={`p-4 rounded-lg border-2 text-left transition-all duration-200 ${
                              activityAnswers[index] === option
                                ? 'border-purple-200 bg-purple-50'
                                : 'border-gray-200 hover:border-purple-100'
                            } ${
                              showActivityResults[index] && activity.answer === option
                                ? 'border-green-200 bg-green-50'
                                : ''
                            } ${
                              showActivityResults[index] && activityAnswers[index] === option && activity.answer !== option
                                ? 'border-red-200 bg-red-50'
                                : ''
                            }`}
                          >
                            <div className="flex items-center">
                              <div className={`w-6 h-6 rounded-full border-2 mr-3 flex items-center justify-center ${
                                activityAnswers[index] === option
                                  ? 'border-purple-200 bg-purple-200 text-purple-900'
                                  : 'border-gray-200'
                              }`}>
                                {activityAnswers[index] === option && '✓'}
                              </div>
                              <span className="font-medium text-gray-900">
                                {option}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Quiz Activity */}
                  {activity.type === 'quiz' && activity.questions && (
                    <div className="space-y-4">
                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-4">
                        <p className="text-blue-700 text-sm">
                          <strong>Quiz:</strong> Test your knowledge with these questions.
                        </p>
                      </div>
                      {activity.questions.map((question: any, qIndex: number) => (
                        <div key={qIndex} className="bg-white rounded-lg p-4 border border-gray-200">
                          <p className="font-medium text-gray-900 mb-3">{question.question}</p>
                          <input
                            type="text"
                            placeholder="Type your answer..."
                            value={activityAnswers[`${index}-${qIndex}`] || ''}
                            onChange={(e) => setActivityAnswers(prev => ({
                              ...prev,
                              [`${index}-${qIndex}`]: e.target.value
                            }))}
                            disabled={showActivityResults[`${index}-${qIndex}`]}
                            className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                          />
                          {showActivityResults[`${index}-${qIndex}`] && (
                            <div className="mt-2 p-2 bg-gray-50 rounded">
                              <p className="text-sm text-gray-600"><strong>Answer:</strong> {question.answer}</p>
                              {question.explanation && (
                                <p className="text-sm text-gray-500 mt-1">{question.explanation}</p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                      {!showActivityResults[index] && (
                        <button
                          onClick={() => {
                            activity.questions.forEach((q: any, qIndex: number) => {
                              const isCorrect = activityAnswers[`${index}-${qIndex}`]?.toLowerCase().includes(q.answer.toLowerCase());
                              setActivityResults(prev => ({ ...prev, [`${index}-${qIndex}`]: isCorrect }));
                              setShowActivityResults(prev => ({ ...prev, [`${index}-${qIndex}`]: true }));
                            });
                          }}
                          className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                          Check Answers
                        </button>
                      )}
                    </div>
                  )}

                  {/* Roleplay Activity */}
                  {activity.type === 'roleplay' && activity.lines && (
                    <div className="space-y-4">
                      <div className="bg-green-50 border border-green-100 rounded-lg p-4 mb-4">
                        <p className="text-green-700 text-sm">
                          <strong>Roleplay:</strong> Practice these phrases with dramatic flair!
                        </p>
                      </div>
                      <div className="space-y-3">
                        {activity.lines.map((line: string, lineIndex: number) => (
                          <div key={lineIndex} className="bg-white rounded-lg p-4 border border-gray-200">
                            <p 
                              className="text-lg font-medium text-gray-900 mb-2"
                              dir={getTextDirection(line)}
                              style={{ textAlign: getTextAlignment(line) }}
                            >
                              {line}
                            </p>
                            <button
                              onClick={() => {
                                // Simulate audio playback
                              }}
                              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
                            >
                              <FaVolumeUp className="inline mr-1" />
                              Practice
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Drag & Drop Activity */}
                  {activity.type === 'drag-drop' && activity.conversations && (
                    <div className="space-y-4">
                      <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 mb-4">
                        <p className="text-purple-700 text-sm">
                          <strong>Drag & Drop:</strong> Arrange the phrases in the correct order.
                        </p>
                      </div>
                      {activity.conversations.map((conversation: any, convIndex: number) => (
                        <div key={convIndex} className="bg-white rounded-lg p-4 border border-gray-200">
                          <h5 className="font-medium text-gray-900 mb-3">{conversation.title}</h5>
                          <div className="space-y-2">
                            {conversation.phrases.map((phrase: string, phraseIndex: number) => (
                              <div key={phraseIndex} className="p-3 bg-gray-50 rounded border border-gray-200">
                                <p 
                                  className="text-gray-900"
                                  dir={getTextDirection(phrase)}
                                  style={{ textAlign: getTextAlignment(phrase) }}
                                >
                                  {phraseIndex + 1}. {phrase}
                                </p>
                              </div>
                            ))}
                          </div>
                          <div className="mt-3 text-sm text-gray-600">
                            <strong>Correct order:</strong> {conversation.correctOrder.join(' → ')}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Interactive Story Activity */}
                  {activity.type === 'interactive-story' && activity.story && (
                    <div className="space-y-4">
                      <div className="bg-orange-50 border border-orange-100 rounded-lg p-4 mb-4">
                        <p className="text-orange-700 text-sm">
                          <strong>Interactive Story:</strong> Help PingQuack navigate through different ta'arof situations!
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <h5 className="font-medium text-gray-900 mb-3">{activity.story.title}</h5>
                        {activity.story.scenarios && activity.story.scenarios.map((scenario: any, scenarioIndex: number) => (
                          <div key={scenarioIndex} className="mb-4 p-3 bg-gray-50 rounded">
                            <p className="text-gray-900 mb-3">{scenario.situation}</p>
                            <div className="space-y-2">
                              {scenario.choices && scenario.choices.map((choice: any, choiceIndex: number) => (
                                <button
                                  key={choiceIndex}
                                  onClick={() => {
                                    // Handle choice selection
                                  }}
                                  className="w-full p-3 text-left bg-white rounded border border-gray-200 hover:bg-gray-50 transition-colors"
                                >
                                  <p className="font-medium text-gray-900">{choice.text}</p>
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Number Recognition Activity */}
                  {activity.type === 'number-recognition' && activity.options && (
                    <div className="space-y-3">
                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-4">
                        <p className="text-blue-700 text-sm">
                          <strong>Number Recognition:</strong> Listen to the Persian numbers and choose the correct option.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {activity.options.map((option: string, optionIndex: number) => (
                          <button
                            key={optionIndex}
                            onClick={() => handleMultipleChoiceAnswer(index, option)}
                            disabled={showActivityResults[index]}
                            className={`p-4 rounded-lg border-2 text-left transition-all duration-200 ${
                              activityAnswers[index] === option
                                ? 'border-purple-200 bg-purple-50'
                                : 'border-gray-200 hover:border-purple-100'
                            } ${
                              showActivityResults[index] && activity.correctAnswer === option
                                ? 'border-green-200 bg-green-50'
                                : ''
                            } ${
                              showActivityResults[index] && activityAnswers[index] === option && activity.correctAnswer !== option
                                ? 'border-red-200 bg-red-50'
                                : ''
                            }`}
                          >
                            <div className="flex items-center">
                              <div className={`w-6 h-6 rounded-full border-2 mr-3 flex items-center justify-center ${
                                activityAnswers[index] === option
                                  ? 'border-purple-200 bg-purple-200 text-purple-900'
                                  : 'border-gray-200'
                              }`}>
                                {activityAnswers[index] === option && '✓'}
                              </div>
                              <span className="font-medium text-gray-900">
                                {option}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                      {!showActivityResults[index] && (
                        <button
                          onClick={() => checkActivityResult(index, activity)}
                          disabled={!activityAnswers[index]}
                          className="px-6 py-3 bg-purple-100 text-purple-900 rounded-lg hover:bg-purple-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Check Answer
                        </button>
                      )}
                    </div>
                  )}

                  {/* Cultural Insight Activity */}
                  {activity.type === 'cultural-insight' && activity.options && (
                    <div className="space-y-3">
                      <div className="bg-orange-50 border border-orange-100 rounded-lg p-4 mb-4">
                        <p className="text-orange-700 text-sm">
                          <strong>Cultural Insight:</strong> Learn about Persian culture and customs.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {activity.options.map((option: string, optionIndex: number) => (
                          <button
                            key={optionIndex}
                            onClick={() => handleMultipleChoiceAnswer(index, option)}
                            disabled={showActivityResults[index]}
                            className={`p-4 rounded-lg border-2 text-left transition-all duration-200 ${
                              activityAnswers[index] === option
                                ? 'border-purple-200 bg-purple-50'
                                : 'border-gray-200 hover:border-purple-100'
                            } ${
                              showActivityResults[index] && activity.correctAnswer === option
                                ? 'border-green-200 bg-green-50'
                                : ''
                            } ${
                              showActivityResults[index] && activityAnswers[index] === option && activity.correctAnswer !== option
                                ? 'border-red-200 bg-red-50'
                                : ''
                            }`}
                          >
                            <div className="flex items-center">
                              <div className={`w-6 h-6 rounded-full border-2 mr-3 flex items-center justify-center ${
                                activityAnswers[index] === option
                                  ? 'border-purple-200 bg-purple-200 text-purple-900'
                                  : 'border-gray-200'
                              }`}>
                                {activityAnswers[index] === option && '✓'}
                              </div>
                              <span className="font-medium text-gray-900">
                                {option}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                      {!showActivityResults[index] && (
                        <button
                          onClick={() => checkActivityResult(index, activity)}
                          disabled={!activityAnswers[index]}
                          className="px-6 py-3 bg-purple-100 text-purple-900 rounded-lg hover:bg-purple-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Check Answer
                        </button>
                      )}
                    </div>
                  )}

                  {/* Pronunciation Practice Activity */}
                  {activity.type === 'pronunciation-practice' && activity.numbers && (
                    <div className="space-y-4">
                      <div className="bg-green-50 border border-green-100 rounded-lg p-4 mb-4">
                        <p className="text-green-700 text-sm">
                          <strong>Pronunciation Practice:</strong> Practice pronouncing Persian numbers clearly.
                        </p>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {activity.numbers.map((number: any, numIndex: number) => (
                          <div key={numIndex} className="bg-white rounded-lg p-4 border border-gray-200 text-center">
                            <div className="text-2xl font-bold text-gray-900 mb-2" dir="rtl">
                              {number.persian}
                            </div>
                            <div className="text-lg text-gray-600 mb-2">
                              {number.pronunciation}
                            </div>
                            <div className="text-sm text-gray-500 mb-3">
                              {number.english}
                            </div>
                            {(
                              <button
                                onClick={() => alert(`Number: ${number.persian}`)}
                                className="p-2 bg-green-100 text-green-900 rounded-full hover:bg-green-200 transition-colors"
                                title="Practice pronunciation"
                              >
                                <FaVolumeUp />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Real-World Application Activity */}
                  {activity.type === 'real-world-application' && activity.scenarios && (
                    <div className="space-y-4">
                      <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 mb-4">
                        <p className="text-purple-700 text-sm">
                          <strong>Real-World Application:</strong> Practice using phone numbers in different contexts.
                        </p>
                      </div>
                      <div className="space-y-4">
                        {activity.scenarios.map((scenario: any, scenIndex: number) => (
                          <div key={scenIndex} className="bg-white rounded-lg p-4 border border-gray-200">
                            <div className="font-semibold text-gray-900 mb-2">
                              {scenario.context}
                            </div>
                            <div className="text-gray-700 mb-3" dir="rtl" style={{ textAlign: 'right' }}>
                              {scenario.phrase}
                            </div>
                            <button
                              onClick={() => alert(`Phrase: ${scenario.phrase}`)}
                              className="px-4 py-2 bg-purple-100 text-purple-900 rounded-lg hover:bg-purple-200 transition-colors"
                            >
                              Practice Speaking
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Dialogue Completion Activity */}
                  {activity.type === 'dialogue-completion' && activity.dialogue && (
                    <div className="space-y-4">
                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-4">
                        <p className="text-blue-700 text-sm">
                          <strong>Dialogue Completion:</strong> Complete the phone number exchange conversation.
                        </p>
                      </div>
                      <div className="space-y-4">
                        {activity.dialogue.map((line: any, lineIndex: number) => (
                          <div key={lineIndex} className="bg-white rounded-lg p-4 border border-gray-200">
                            <div className="font-semibold text-gray-900 mb-2">{line.speaker}</div>
                            <div className="text-gray-700 mb-2" dir="rtl" style={{ textAlign: 'right' }}>
                              {line.text}
                            </div>
                            <div className="text-gray-600">{line.translation}</div>
                          </div>
                        ))}
                      </div>
                      {activity.options && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                          {activity.options.map((option: string, optionIndex: number) => (
                            <button
                              key={optionIndex}
                              onClick={() => handleMultipleChoiceAnswer(index, option)}
                              disabled={showActivityResults[index]}
                              className={`p-4 rounded-lg border-2 text-left transition-all duration-200 ${
                                activityAnswers[index] === option
                                  ? 'border-purple-200 bg-purple-50'
                                  : 'border-gray-200 hover:border-purple-100'
                              }`}
                            >
                              <span className="font-medium text-gray-900">{option}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Comprehensive Review Activity */}
                  {activity.type === 'comprehensive-review' && activity.prompt && (
                    <div className="space-y-4">
                      <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 mb-4">
                        <p className="text-indigo-700 text-sm">
                          <strong>Comprehensive Review:</strong> Practice giving all your contact information.
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <div className="text-gray-700 mb-4">{activity.prompt}</div>
                        {activity.example && (
                          <div className="bg-gray-50 rounded-lg p-4 mb-4">
                            <div className="text-sm text-gray-600 mb-2">Example:</div>
                            <div className="text-gray-800" dir="rtl" style={{ textAlign: 'right' }}>
                              {activity.example}
                            </div>
                          </div>
                        )}
                        <button
                          onClick={() => alert(`Example: ${activity.example || ''}`)}
                          className="px-4 py-2 bg-indigo-100 text-indigo-900 rounded-lg hover:bg-indigo-200 transition-colors"
                        >
                          Practice Complete Introduction
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Cultural Note Activity */}
                  {activity.type === 'cultural-note' && activity.options && (
                    <div className="space-y-3">
                      <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 mb-4">
                        <p className="text-amber-700 text-sm">
                          <strong>Cultural Note:</strong> Learn about cultural aspects of communication in Persian.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {activity.options.map((option: string, optionIndex: number) => (
                          <button
                            key={optionIndex}
                            onClick={() => handleMultipleChoiceAnswer(index, option)}
                            disabled={showActivityResults[index]}
                            className={`p-4 rounded-lg border-2 text-left transition-all duration-200 ${
                              activityAnswers[index] === option
                                ? 'border-purple-200 bg-purple-50'
                                : 'border-gray-200 hover:border-purple-100'
                            } ${
                              showActivityResults[index] && activity.correctAnswer === option
                                ? 'border-green-200 bg-green-50'
                                : ''
                            } ${
                              showActivityResults[index] && activityAnswers[index] === option && activity.correctAnswer !== option
                                ? 'border-red-200 bg-red-50'
                                : ''
                            }`}
                          >
                            <div className="flex items-center">
                              <div className={`w-6 h-6 rounded-full border-2 mr-3 flex items-center justify-center ${
                                activityAnswers[index] === option
                                  ? 'border-purple-200 bg-purple-200 text-purple-900'
                                  : 'border-gray-200'
                              }`}>
                                {activityAnswers[index] === option && '✓'}
                              </div>
                              <span className="font-medium text-gray-900">
                                {option}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                      {!showActivityResults[index] && (
                        <button
                          onClick={() => checkActivityResult(index, activity)}
                          disabled={!activityAnswers[index]}
                          className="px-6 py-3 bg-purple-100 text-purple-900 rounded-lg hover:bg-purple-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Check Answer
                        </button>
                      )}
                    </div>
                  )}

                  {/* Scenario Practice Activity */}
                  {activity.type === 'scenario-practice' && activity.scenario && (
                    <div className="space-y-4">
                      <div className="bg-teal-50 border border-teal-100 rounded-lg p-4 mb-4">
                        <p className="text-teal-700 text-sm">
                          <strong>Scenario Practice:</strong> Practice real-world conversations.
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <div className="font-semibold text-gray-900 mb-2">Scenario:</div>
                        <div className="text-gray-700 mb-4">{activity.scenario}</div>
                        {activity.keyPhrases && (
                          <div className="mb-4">
                            <div className="font-semibold text-gray-900 mb-2">Key Phrases:</div>
                            <div className="space-y-2">
                              {activity.keyPhrases.map((phrase: string, phraseIndex: number) => (
                                <div key={phraseIndex} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                                  <span className="text-gray-800" dir="rtl" style={{ textAlign: 'right' }}>
                                    {phrase}
                                  </span>
                                  <button
                                    onClick={() => alert(`Phrase: ${phrase}`)}
                                    className="p-2 bg-teal-100 text-teal-900 rounded-full hover:bg-teal-200 transition-colors"
                                  >
                                    <FaVolumeUp />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="text-gray-600">{activity.practice}</div>
                      </div>
                    </div>
                  )}

                  {/* Activity Results */}
                  {showActivityResults[index] && (
                    <div className={
                      activityResults[index]
                        ? 'mt-4 p-4 rounded-lg bg-green-100 dark:bg-green-800 border border-green-300 dark:border-green-600'
                        : 'mt-4 p-4 rounded-lg bg-red-100 dark:bg-red-800 border border-red-300 dark:border-red-600'
                    }>
                      <div className="flex items-center">
                        {activityResults[index] ? (
                          <>
                            <FaCheck className="text-green-600 dark:text-green-400 mr-2" />
                            <span className="text-green-800 dark:text-green-200 font-medium">
                              Correct! Well done!
                            </span>
                          </>
                        ) : (
                          <>
                            <FaTimes className="text-red-600 dark:text-red-400 mr-2" />
                            <span className="text-red-800 dark:text-red-200 font-medium">
                              Not quite right. Try again!
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="col-span-full text-center text-gray-500">
                No activities available for this lesson.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCompleteScreen = () => {
    return (
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden p-8 text-center">
        <h2 className="text-4xl font-bold text-gray-900 mb-4">
          <FaCheck className="text-green-500 mr-3" />
          Lesson Complete!
        </h2>
        <p className="text-lg text-gray-700 mb-6">
          You have completed the lesson. Your score is {getActivityScore()}%!
        </p>
        <Link to={`/pathways/${pathwayId}`}>
          <button className="px-8 py-4 rounded-lg font-bold text-white bg-blue-500 hover:bg-blue-600 transition-colors">
            Back to Pathway
          </button>
        </Link>
      </div>
    );
  };

  if (loading) {
    return <div className="text-center py-10">Loading...</div>;
  }

  if (!pathway || !lesson) {
    return <div className="text-center py-10">Lesson not found.</div>;
  }

  // Find the scenes array from lessonContent

  // Scene navigation logic
  const handlePrevScene = () => {
    setCurrentSceneIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };
  const handleNextScene = () => {
    setCurrentSceneIndex((prev) => (prev < (content?.scenes?.length || 0) - 1 ? prev + 1 : prev));
  };

  // Enhanced navigation component
  const renderEnhancedNavigation = () => {
    if ((content?.scenes?.length || 0) <= 1) return null;

    return (
      <div className="fixed top-1/2 transform -translate-y-1/2 z-50 flex items-center justify-between w-full px-4 pointer-events-none">
        {/* Left Navigation Button */}
        <button
          onClick={handlePrevScene}
          disabled={currentSceneIndex === 0}
          className={`pointer-events-auto p-4 rounded-full shadow-2xl transition-all duration-300 transform hover:scale-125 ${
            currentSceneIndex === 0
              ? 'bg-gray-400 text-gray-600 cursor-not-allowed opacity-60'
              : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 border-4 border-white shadow-2xl hover:shadow-3xl'
          }`}
          aria-label="Previous Scene"
        >
          <FaChevronLeft className="w-8 h-8 font-bold" />
        </button>

        {/* Right Navigation Button */}
        <button
          onClick={handleNextScene}
          disabled={currentSceneIndex === scenes.length - 1}
          className={`pointer-events-auto p-4 rounded-full shadow-2xl transition-all duration-300 transform hover:scale-125 ${
            currentSceneIndex === scenes.length - 1
              ? 'bg-gray-400 text-gray-600 cursor-not-allowed opacity-60'
              : 'bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800 border-4 border-white shadow-2xl hover:shadow-3xl'
          }`}
          aria-label="Next Scene"
        >
          <FaChevronRight className="w-8 h-8 font-bold" />
        </button>
      </div>
    );
  };

  // Step indicator component
  const renderStepIndicator = () => {
    const currentStepIndex = steps.findIndex(step => step.key === currentStep);

    return (
      <div className="mb-8 relative">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 rounded-3xl transform rotate-1 scale-105 opacity-30"></div>
        
        <div className="relative bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl p-8 border border-white/20 overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full -translate-y-10 translate-x-10 opacity-40"></div>
          <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-pink-100 to-purple-100 rounded-full translate-y-8 -translate-x-8 opacity-40"></div>
          
          <div className="relative z-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center justify-center">
              <FaRoad className="mr-3 text-blue-500 text-2xl" />
              Learning Journey
            </h3>
            
            {/* Step Progress Bar */}
            <div className="flex flex-col sm:flex-row items-center justify-between mb-6 lg:mb-8 space-y-4 sm:space-y-0">
              {steps.map((step, index) => {
                const isActive = step.key === currentStep;
                const isCompleted = index < currentStepIndex;
                const IconComponent = step.icon;
                
                return (
                  <div key={step.key} className="flex flex-col items-center flex-1 min-w-0">
                    <div className="relative mb-3 lg:mb-4">
                      <div className={`flex items-center justify-center w-12 h-12 lg:w-16 lg:h-16 rounded-xl lg:rounded-2xl border-2 transition-all duration-500 transform hover:scale-110 ${
                        isActive 
                          ? index === 0 ? 'border-blue-500 bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-2xl scale-110'
                            : index === 1 ? 'border-green-500 bg-gradient-to-br from-green-500 to-green-600 text-white shadow-2xl scale-110'
                            : index === 2 ? 'border-purple-500 bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-2xl scale-110'
                            : 'border-emerald-500 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-2xl scale-110'
                          : isCompleted
                          ? 'border-green-500 bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg'
                          : 'border-gray-300 bg-gray-100 text-gray-400 hover:border-gray-400 hover:bg-gray-200'
                      }`}>
                        <IconComponent className="w-6 h-6 lg:w-8 lg:h-8" />
                      </div>
                      
                      {/* Progress indicator */}
                      {isActive && (
                        <div className="absolute -top-1 -right-1 lg:-top-2 lg:-right-2 w-4 h-4 lg:w-6 lg:h-6 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                          <span className="text-white text-xs font-bold">!</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="text-center px-2">
                      <div className={`font-bold text-sm lg:text-lg transition-colors duration-300 ${
                        isActive 
                          ? index === 0 ? 'text-blue-600'
                            : index === 1 ? 'text-green-600'
                            : index === 2 ? 'text-purple-600'
                            : 'text-emerald-600'
                          : isCompleted ? 'text-green-600' : 'text-gray-500'
                      }`}>
                        {step.label}
                      </div>
                      <div className="text-xs lg:text-sm text-gray-600 mt-1 max-w-20 lg:max-w-24">
                        {step.description}
                      </div>
                    </div>
                    
                    {/* Connection line */}
                    {index < steps.length - 1 && (
                      <div className="absolute top-8 left-1/2 w-full h-1 z-0">
                        <div className={`h-1 rounded-full transition-all duration-500 ${
                          index < currentStepIndex ? 'bg-gradient-to-r from-green-500 to-green-400' : 'bg-gray-300'
                        }`} style={{ width: 'calc(100% - 4rem)', marginLeft: '2rem' }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Current Step Info */}
            <div className="text-center bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-200">
              <div className="flex items-center justify-center mb-2">
                <div className={`w-3 h-3 rounded-full mr-2 ${
                  currentStepIndex === 0 ? 'bg-blue-500'
                  : currentStepIndex === 1 ? 'bg-green-500'
                  : currentStepIndex === 2 ? 'bg-purple-500'
                  : 'bg-emerald-500'
                } animate-pulse`}></div>
                <span className="text-lg font-bold text-gray-800">
                  {steps[currentStepIndex]?.label || 'Unknown Step'}
                </span>
              </div>
              <p className="text-sm text-gray-600">
                Step {currentStepIndex + 1} of {steps.length} • {steps[currentStepIndex]?.description}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Enhanced progress indicator
  const renderProgressIndicator = () => {
    if (currentStep !== 'scenes' || (content?.scenes?.length || 0) <= 1) return null;

    return (
      <div className="mb-8 relative">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-3xl transform rotate-1 scale-105 opacity-40"></div>
        
        <div className="relative bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl p-8 border border-white/20 overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full -translate-y-12 translate-x-12 opacity-50"></div>
          <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-purple-100 to-pink-100 rounded-full translate-y-10 -translate-x-10 opacity-50"></div>
          
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-800 flex items-center">
                <FaBook className="mr-3 text-blue-600 text-2xl" />
                Scene Navigation
              </h3>
              <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-2xl shadow-lg font-bold text-lg">
                {currentSceneIndex + 1} of {content?.scenes?.length || 0}
              </div>
            </div>
            
            {/* Enhanced Progress Bar */}
            <div className="mb-6">
              <div className="w-full bg-gray-200 rounded-full h-6 mb-4 shadow-inner overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 rounded-full transition-all duration-1000 ease-out relative overflow-hidden"
                  style={{ width: `${((currentSceneIndex + 1) / (content?.scenes?.length || 1)) * 100}%` }}
                >
                  <div className="absolute inset-0 bg-white bg-opacity-30 animate-pulse"></div>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-pulse"></div>
                  <div className="absolute right-0 top-0 w-8 h-full bg-gradient-to-l from-white to-transparent"></div>
                </div>
              </div>
              
              {/* Progress percentage */}
              <div className="text-center">
                <span className="text-2xl font-bold text-gray-800">
                  {Math.round(((currentSceneIndex + 1) / (content?.scenes?.length || 1)) * 100)}%
                </span>
                <span className="text-sm text-gray-600 ml-2">Complete</span>
              </div>
            </div>

            {/* Enhanced Scene Dots */}
            <div className="flex justify-center space-x-4 mb-6">
              {scenes.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSceneIndex(index)}
                  className={`relative w-8 h-8 rounded-full transition-all duration-300 shadow-lg transform hover:scale-125 ${
                    index === currentSceneIndex
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 scale-150 shadow-2xl border-4 border-white'
                      : index < currentSceneIndex
                      ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 border-2 border-green-300'
                      : 'bg-gray-400 hover:bg-gray-500 border-2 border-gray-300'
                  }`}
                  aria-label={`Go to scene ${index + 1}`}
                >
                  {index === currentSceneIndex && (
                    <div className="absolute -top-2 -right-2 w-4 h-4 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                      <span className="text-white text-xs font-bold">!</span>
                    </div>
                  )}
                </button>
              ))}
            </div>

            {/* Scene Title and Navigation */}
            <div className="flex justify-between items-center">
              <div className="flex-1 mr-6">
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/20">
                  <h4 className="text-lg font-bold text-gray-800 mb-1">
                    {(content?.scenes?.[currentSceneIndex] as any)?.title || `Scene ${currentSceneIndex + 1}`}
                  </h4>
                  <p className="text-sm text-gray-600">
                    Interactive learning scenario
                  </p>
                </div>
              </div>
              
              <div className="flex space-x-4">
                <button
                  onClick={handlePrevScene}
                  disabled={currentSceneIndex === 0}
                  className={`flex items-center space-x-3 px-6 py-4 rounded-2xl font-bold text-lg transition-all duration-300 shadow-lg transform hover:scale-105 ${
                    currentSceneIndex === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                      : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 hover:shadow-xl'
                  }`}
                >
                  <FaChevronLeft className="w-5 h-5" />
                  <span>Previous</span>
                </button>
                <button
                  onClick={handleNextScene}
                  disabled={currentSceneIndex === (content?.scenes?.length || 0) - 1}
                  className={`flex items-center space-x-3 px-6 py-4 rounded-2xl font-bold text-lg transition-all duration-300 shadow-lg transform hover:scale-105 ${
                    currentSceneIndex === (content?.scenes?.length || 0) - 1
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                      : 'bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800 hover:shadow-xl'
                  }`}
                >
                  <span>Next</span>
                  <FaChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 text-gray-900 relative overflow-hidden" role="main" aria-label="Rich Content Lesson">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5" aria-hidden="true">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>
      
      {/* Enhanced Navigation Overlay */}
      {renderEnhancedNavigation()}
      
      {/* Keyboard Shortcuts Modal */}
      {renderKeyboardShortcuts()}
      
      <div className="container mx-auto p-4 relative z-10">
        {/* Sophisticated Breadcrumb Navigation */}
        <nav className="flex items-center space-x-2 mb-8 text-sm" aria-label="Breadcrumb navigation">
          <Link 
            to="/pathways" 
            className="flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200 hover:scale-105 transform"
            aria-label="Go back to pathways list"
          >
            <span className="bg-blue-100 px-3 py-1 rounded-full font-medium">Pathways</span>
          </Link>
          <span className="text-gray-400" aria-hidden="true">›</span>
          <Link 
            to={`/pathways/${pathwayId}`} 
            className="flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200 hover:scale-105 transform"
            aria-label={`Go back to ${pathway?.title || 'pathway'} details`}
          >
            <span className="bg-blue-100 px-3 py-1 rounded-full font-medium">{pathway?.title || 'Pathway'}</span>
          </Link>
          <span className="text-gray-400" aria-hidden="true">›</span>
          <span className="bg-gradient-to-r from-purple-100 to-pink-100 px-3 py-1 rounded-full font-medium text-purple-800" aria-current="page">
            {lesson?.title || 'Lesson'}
          </span>
        </nav>

        {loading ? (
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              {/* Sophisticated Loading Animation */}
              <div className="relative mb-8">
                <div className="w-20 h-20 mx-auto">
                  <div className="absolute inset-0 rounded-full border-4 border-blue-200"></div>
                  <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-600 animate-spin"></div>
                  <div className="absolute inset-2 rounded-full border-4 border-transparent border-r-purple-600 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
                </div>
                <div className="mt-4 flex justify-center space-x-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Loading Your Lesson</h3>
                <p className="text-lg text-gray-600 mb-4">Preparing an immersive learning experience...</p>
                <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
                  <span className="bg-blue-100 px-3 py-1 rounded-full">Pathway: {pathwayId}</span>
                  <span className="bg-purple-100 px-3 py-1 rounded-full">Lesson: {lessonId}</span>
                </div>
              </div>
            </div>
          </div>
        ) : lesson ? (
          <>
            {/* Sophisticated Lesson Header */}
            <div className="relative mb-8">
              {/* Background Gradient */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-3xl transform rotate-1 scale-105 opacity-10" aria-hidden="true"></div>
              
              <div className="relative bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-8 border border-white/20 overflow-hidden">
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full -translate-y-16 translate-x-16 opacity-50" aria-hidden="true"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-pink-100 to-purple-100 rounded-full translate-y-12 -translate-x-12 opacity-50" aria-hidden="true"></div>
                
                <div className="relative z-10">
                  <header className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 space-y-4 lg:space-y-0">
                    <div className="flex items-center space-x-4 lg:space-x-6">
                      {/* Lesson Icon */}
                      <div className="relative">
                        <div className="w-12 h-12 lg:w-16 lg:h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg transform rotate-3 hover:rotate-0 transition-transform duration-300" aria-hidden="true">
                          <FaBook className="text-white text-lg lg:text-2xl" />
                        </div>
                        <div className="absolute -top-1 -right-1 lg:-top-2 lg:-right-2 w-4 h-4 lg:w-6 lg:h-6 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-md" aria-label={`Lesson ${lesson.order}`}>
                          <span className="text-white text-xs font-bold">{lesson.order}</span>
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h1 className="text-2xl lg:text-4xl font-bold bg-gradient-to-r from-gray-800 via-blue-800 to-purple-800 bg-clip-text text-transparent mb-2 break-words">
                          {lesson.title}
                        </h1>
                        {lesson.description && (
                          <p className="text-base lg:text-lg text-gray-600 leading-relaxed max-w-2xl">
                            {lesson.description}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center justify-center lg:justify-end space-x-3 lg:space-x-4" role="group" aria-label="Lesson actions">
                      <button
                        onClick={() => setShowKeyboardShortcuts(true)}
                        className="group relative p-3 lg:p-4 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-xl lg:rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                        title="Show keyboard shortcuts (Press ?)"
                        aria-label="Show keyboard shortcuts help"
                      >
                        <FaKeyboard className="w-5 h-5 lg:w-6 lg:h-6 group-hover:scale-110 transition-transform duration-300" />
                        <div className="absolute -top-1 -right-1 lg:-top-2 lg:-right-2 w-2 h-2 lg:w-3 lg:h-3 bg-yellow-400 rounded-full animate-pulse" aria-hidden="true"></div>
                      </button>
                      
                      <button
                        onClick={handleRefresh}
                        className="group relative p-3 lg:p-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl lg:rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                        title="Refresh lesson data to get latest audio URLs"
                        aria-label="Refresh lesson data"
                      >
                        <svg className="w-5 h-5 lg:w-6 lg:h-6 group-hover:rotate-180 transition-transform duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        <div className="absolute -top-1 -right-1 lg:-top-2 lg:-right-2 w-2 h-2 lg:w-3 lg:h-3 bg-green-400 rounded-full animate-pulse" aria-hidden="true"></div>
                      </button>
                      
                      {lessonProgress > 0 && (
                        <div className="flex items-center space-x-2 lg:space-x-4 bg-white/80 backdrop-blur-sm rounded-xl lg:rounded-2xl p-3 lg:p-4 shadow-lg border border-white/20">
                          <div className="text-center">
                            <div className="text-lg lg:text-2xl font-bold text-gray-800">{lessonProgress}%</div>
                            <div className="text-xs lg:text-sm text-gray-600">Complete</div>
                          </div>
                          <div className="w-16 lg:w-24 h-2 lg:h-3 bg-gray-200 rounded-full overflow-hidden shadow-inner">
                            <div 
                              className="h-full bg-gradient-to-r from-green-400 via-blue-500 to-purple-600 transition-all duration-1000 ease-out relative"
                              style={{ width: `${lessonProgress}%` }}
                            >
                              <div className="absolute inset-0 bg-white bg-opacity-30 animate-pulse"></div>
                              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-pulse"></div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </header>
                  
                  {/* Enhanced Status Indicators */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
                    {/* Audio Status */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-4 shadow-sm">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div>
                          <div className="font-semibold text-green-800">Audio Ready</div>
                          <div className="text-sm text-green-600">High-quality Persian audio</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Lesson Info */}
                    {lesson.goal && (
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-4 shadow-sm">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                            <FaRoad className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <div className="font-semibold text-blue-800">Learning Goal</div>
                            <div className="text-sm text-blue-600">{lesson.goal}</div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Duration */}
                    {lesson.duration && (
                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-2xl p-4 shadow-sm">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div>
                            <div className="font-semibold text-purple-800">Duration</div>
                            <div className="text-sm text-purple-600">{lesson.duration}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Enhanced Progress Indicators */}
                  {content && (
                    <div className="mt-6 bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                      <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                        <FaRoad className="mr-2 text-blue-500" />
                        Learning Progress
                      </h3>
                      <div className="flex items-center justify-center space-x-8">
                        <div className="flex items-center space-x-3">
                          <div className={`w-4 h-4 rounded-full transition-all duration-300 ${sectionProgress['scenes'] ? 'bg-gradient-to-r from-green-400 to-emerald-500 animate-pulse shadow-lg' : 'bg-gray-300'}`}></div>
                          <span className={`text-sm font-medium transition-colors duration-300 ${sectionProgress['scenes'] ? 'text-green-600' : 'text-gray-500'}`}>
                            Scenes ({completedScenes.length}/{content.scenes?.length || 0})
                          </span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className={`w-4 h-4 rounded-full transition-all duration-300 ${sectionProgress['wordbank'] ? 'bg-gradient-to-r from-blue-400 to-indigo-500 animate-pulse shadow-lg' : 'bg-gray-300'}`}></div>
                          <span className={`text-sm font-medium transition-colors duration-300 ${sectionProgress['wordbank'] ? 'text-blue-600' : 'text-gray-500'}`}>
                            Vocabulary
                          </span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className={`w-4 h-4 rounded-full transition-all duration-300 ${sectionProgress['activities'] ? 'bg-gradient-to-r from-purple-400 to-pink-500 animate-pulse shadow-lg' : 'bg-gray-300'}`}></div>
                          <span className={`text-sm font-medium transition-colors duration-300 ${sectionProgress['activities'] ? 'text-purple-600' : 'text-gray-500'}`}>
                            Activities ({Object.keys(activityResults).filter(key => activityResults[key]).length}/{content.activities?.length || 0})
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Step Indicator */}
            {renderStepIndicator()}

            {/* Enhanced Progress Indicator */}
            {renderProgressIndicator()}

            {/* Debug section - remove in production */}
            {process.env.NODE_ENV === 'development' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-semibold text-yellow-800 mb-2">Debug Info</h3>
                <div className="text-sm text-yellow-700">
                  <p><strong>Pathway ID:</strong> {pathwayId}</p>
                  <p><strong>Lesson ID:</strong> {lessonId}</p>
                  <p><strong>Lesson Title:</strong> {lesson.title}</p>
                  <p><strong>Content Scenes:</strong> {content?.scenes?.length || 0}</p>
                  <p><strong>Current Scene Index:</strong> {currentSceneIndex}</p>
                  <p><strong>Current Step:</strong> {currentStep}</p>
                  <p><strong>Content Structure:</strong> <pre className="text-xs mt-2 bg-gray-100 p-2 rounded overflow-auto max-h-32">{JSON.stringify(content, null, 2)}</pre></p>
                  {content?.scenes && content.scenes.length > 0 && (
                    <div className="mt-2">
                      <p><strong>Scene Details:</strong></p>
                      {content.scenes.map((scene: any, idx: number) => (
                        <div key={idx} className="ml-4 text-xs">
                          <p>Scene {idx}: {scene.title || 'No title'} - Type: {scene.type || 'unknown'} - Dialogue lines: {scene.dialogue?.length || 0}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Render the current scene */}
            {(() => {
              console.log('🎭 Rendering scene at index:', currentSceneIndex, 'Scene:', content?.scenes?.[currentSceneIndex]);
              return null;
            })()}
            {content?.scenes && content.scenes.length > 0 && content.scenes[currentSceneIndex] ? (
              <div className="mb-8">
                {renderScene(content.scenes[currentSceneIndex], currentSceneIndex)}
              </div>
            ) : content?.scenes && content.scenes.length > 0 ? (
              <div className="mb-8 text-center">
                <p className="text-gray-600">No scenes available for this lesson.</p>
              </div>
            ) : (
              <div className="mb-8 text-center">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-red-800 mb-2">No Scenes Found</h3>
                  <p className="text-red-700 mb-4">
                    This lesson doesn't have any scenes to display. This might be due to:
                  </p>
                  <ul className="text-red-700 text-left max-w-md mx-auto mb-4">
                    <li className="mb-1">• Content not properly loaded</li>
                    <li className="mb-1">• Lesson structure is incomplete</li>
                    <li className="mb-1">• Content parsing error</li>
                  </ul>
                  <div className="text-sm text-red-600">
                    <p><strong>Content state:</strong> {content ? 'Loaded' : 'Not loaded'}</p>
                    <p><strong>Scenes count:</strong> {content?.scenes?.length || 0}</p>
                    <p><strong>Current step:</strong> {currentStep}</p>
                    <p><strong>Current scene index:</strong> {currentSceneIndex}</p>
                    <p><strong>Content structure:</strong> <pre className="text-xs mt-2 bg-gray-100 p-2 rounded overflow-auto max-h-32">{JSON.stringify(content, null, 2)}</pre></p>
                  </div>
                </div>
              </div>
            )}

            {/* Render step-specific content */}
            {currentStep === 'wordbank' && renderWordBank()}

            {currentStep === 'activities' && renderActivities()}

            {currentStep === 'complete' && renderCompleteScreen()}

            <div className="mt-8 flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0 lg:space-x-4">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep === 'scenes'}
                className="group flex items-center px-6 lg:px-8 py-3 lg:py-4 rounded-xl lg:rounded-2xl font-bold text-base lg:text-lg transition-all duration-300 bg-gradient-to-r from-gray-200 to-gray-300 text-gray-800 hover:from-gray-300 hover:to-gray-400 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105 disabled:hover:scale-100 w-full lg:w-auto"
              >
                <FaChevronLeft className="mr-2 lg:mr-3 group-hover:-translate-x-1 transition-transform duration-300" />
                Previous Step
              </button>
              
              {/* Step-specific info */}
              <div className="text-center order-first lg:order-none">
                {currentStep === 'wordbank' && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 px-4 lg:px-6 py-2 lg:py-3 rounded-xl lg:rounded-2xl shadow-lg">
                    <div className="flex items-center justify-center text-green-700 font-semibold text-sm lg:text-base">
                      <FaLightbulb className="mr-2 text-base lg:text-lg" />
                      <span className="hidden sm:inline">Review vocabulary words and their meanings</span>
                      <span className="sm:hidden">Vocabulary Review</span>
                    </div>
                  </div>
                )}
                {currentStep === 'activities' && (
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 px-4 lg:px-6 py-2 lg:py-3 rounded-xl lg:rounded-2xl shadow-lg">
                    <div className="flex items-center justify-center text-purple-700 font-semibold text-sm lg:text-base">
                      <FaGamepad className="mr-2 text-base lg:text-lg" />
                      <span className="hidden sm:inline">Test your knowledge with interactive exercises</span>
                      <span className="sm:hidden">Practice Exercises</span>
                    </div>
                  </div>
                )}
                {currentStep === 'scenes' && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 px-4 lg:px-6 py-2 lg:py-3 rounded-xl lg:rounded-2xl shadow-lg">
                    <div className="flex items-center justify-center text-blue-700 font-semibold text-sm lg:text-base">
                      <FaBook className="mr-2 text-base lg:text-lg" />
                      <span className="hidden sm:inline">Interactive learning scenarios</span>
                      <span className="sm:hidden">Learning Scenarios</span>
                    </div>
                  </div>
                )}
                {currentStep === 'complete' && (
                  <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 px-4 lg:px-6 py-2 lg:py-3 rounded-xl lg:rounded-2xl shadow-lg">
                    <div className="flex items-center justify-center text-emerald-700 font-semibold text-sm lg:text-base">
                      <FaCheck className="mr-2 text-base lg:text-lg" />
                      <span className="hidden sm:inline">Congratulations! Lesson completed</span>
                      <span className="sm:hidden">Lesson Complete!</span>
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={handleNextStep}
                disabled={currentStep === 'complete'}
                className="group flex items-center px-6 lg:px-8 py-3 lg:py-4 rounded-xl lg:rounded-2xl font-bold text-base lg:text-lg transition-all duration-300 bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105 disabled:hover:scale-100 w-full lg:w-auto"
              >
                {currentStep === 'activities' ? 'Complete Lesson' : 'Next Step'}
                <FaChevronRight className="ml-2 lg:ml-3 group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <div className="relative mb-8">
              {/* Background gradient */}
              <div className="absolute inset-0 bg-gradient-to-r from-red-50 via-orange-50 to-yellow-50 rounded-3xl transform rotate-1 scale-105 opacity-30"></div>
              
              <div className="relative bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-12 border border-white/20 overflow-hidden">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-red-100 to-orange-100 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-yellow-100 to-orange-100 rounded-full translate-y-12 -translate-x-12 opacity-50"></div>
                
                <div className="relative z-10">
                  <div className="text-8xl mb-6">📚</div>
                  <h2 className="text-4xl font-bold bg-gradient-to-r from-gray-800 via-red-600 to-orange-600 bg-clip-text text-transparent mb-4">
                    Lesson Not Found
                  </h2>
                  <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
                    The lesson you're looking for could not be found. This might be because:
                  </p>
                  
                  <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-6 mb-8 border border-red-200 max-w-md mx-auto">
                    <ul className="text-gray-700 text-left space-y-3">
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                        The lesson ID is incorrect
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                        The pathway doesn't exist
                      </li>
                      <li className="flex items-center">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></div>
                        There was a server error
                      </li>
                    </ul>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link 
                      to="/pathways" 
                      className="group px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 font-bold text-lg"
                    >
                      <span className="flex items-center justify-center">
                        <FaArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform duration-300" />
                        Back to Pathways
                      </span>
                    </Link>
                    <button 
                      onClick={() => window.location.reload()} 
                      className="group px-8 py-4 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-2xl hover:from-gray-700 hover:to-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 font-bold text-lg"
                    >
                      <span className="flex items-center justify-center">
                        <svg className="w-5 h-5 mr-2 group-hover:rotate-180 transition-transform duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Try Again
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(RichContentLesson);